<template>
  <div class="welcome-modal">
    <div class="modal-content">
      <div class="posts-container" style="display:flex; flex-direction: column;">
        
        <!-- Compact Sticky User Header Bar -->
        <div class="user-header-bar" style="order:-1000000;">
          <div class="user-info">
            <img 
              v-if="user?.photoURL" 
              :src="user.photoURL" 
              alt="Profile Picture" 
              class="user-avatar"
            />
            <div class="user-details">
              <span class="user-name">{{ user?.displayName || 'User' }}</span>
              <span class="user-role">Private APP Owner</span>
            </div>
          </div>
          <button v-if="isInitialSetup && currentStep === 1" class="setup-continue-btn" @click="startProfileSetup">
            Setup →
          </button>
        </div>

        

        <!-- AI Command (priority above showcases) -->
        <div v-if="showAICommand" class="post ai-command-post" :key="aiKey" :style="{ order: -getOrder('ai') }">
          <h3>🤖 AI Assistant</h3>
          <div class="ai-form" :class="{ 'ai-disabled': aiCardDisabled }">
            <div class="form-group ai-input-group">
              <label class="ai-label">Ask AI anything:</label>
              <textarea 
                v-model="aiPrompt" 
                ref="aiTextarea"
                placeholder="Try: 'Create a note about morning routines' or 'Create category Travel' or 'Add https://claude.ai to AI Tools #assistant'"
                rows="6"
                class="ai-textarea"
              ></textarea>
            </div>
            <div class="ai-actions">
              <button class="action-button ai-execute-btn" @click="executeAICommand" :disabled="!aiPrompt.trim() || aiLoading || aiCardDisabled">
                {{ aiLoading ? '🤖 Processing...' : '✨ Execute Command' }}
              </button>
              <button class="cancel-button" @click="hideAIPrompt">
                Cancel
              </button>
            </div>
            <div v-if="aiResponse" class="ai-response">
              <h4>🤖 AI Response:</h4>
              <p>{{ aiResponse }}</p>
            </div>
          </div>
          <div v-if="aiCardDisabled" class="ai-disabled-banner">
            <span>Chat paused after last action.</span>
            <button class="chooser-pill" @click="enableAIChat">Continue chat</button>
          </div>
        </div>

        <!-- Tag Chooser (appears when multiple items match) -->
        <div v-if="showTagChooser" class="post chooser-post" :key="tagChooserKey" :style="{ order: -getOrder('tag-chooser') }">
          <h3>🔎 Select item to {{ tagChooserAction?.action === 'add' ? 'add' : 'remove' }} #{{ tagChooserAction?.tag }}</h3>
          <p v-if="tagChooserAction?.title">Matches for "{{ tagChooserAction.title }}"</p>
          <div class="chooser-pills">
            <button v-for="cand in tagChooserCandidates" :key="cand.id" class="chooser-pill" @click="selectTagChooserCandidate(cand)">
              <span class="pill-icon">{{ getItemIcon(cand.category) }}</span>
              <span class="pill-title">{{ cand.title || cand.name }}</span>
              <span class="pill-meta">{{ cand.category }}</span>
            </button>
          </div>
          <div class="chooser-actions">
            <button class="cancel-button" @click="cancelTagChooser">Cancel</button>
          </div>
        </div>

        <!-- AI Answer (top-of-feed) -->
        <div v-if="showAIAnswer" class="post ai-answer-post" :key="aiAnswerKey" :style="{ order: -getOrder('ai-answer') }">
          <h3>🤖 AI Answer</h3>
          <div class="ai-answer-content">
            <p>{{ aiAnswerText }}</p>
          </div>
          <div class="chooser-pills">
            <button class="chooser-pill" @click="continueChatFromAnswer">💬 Continue chat</button>
            <button class="chooser-pill" @click="createNoteFromAnswer">📝 Create note</button>
            <button class="chooser-pill" @click="dismissAIAnswer">✓ Done</button>
          </div>
        </div>

        <!-- Tag Operation Success Card -->
        <div v-if="showTagOpSuccess && tagOpSuccessData" class="post tagop-success-post" :key="tagOpSuccessKey" :style="{ order: -getOrder('tag-op-success') }">
          <h3>✅ {{ tagOpSuccessData.action === 'add' ? 'Added' : 'Removed' }} #{{ tagOpSuccessData.tag }} to {{ getItemTypeDisplay(tagOpSuccessData.item.category) }} "{{ tagOpSuccessData.item.title || tagOpSuccessData.item.name }}"</h3>
          <div class="chooser-pills">
            <button class="chooser-pill" @click="viewTagOpItem">👁 See {{ getItemTypeDisplay(tagOpSuccessData.item.category) }}</button>
            <button class="chooser-pill" @click="askAIFromTagOp">🤖 Ask AI</button>
            <button class="chooser-pill" @click="undoTagOp">↩ Undo</button>
            <button class="chooser-pill" @click="dismissTagOpSuccess">✓ Done</button>
          </div>
        </div>

        <!-- Creation Context Cards (priority above showcases) -->
        <!-- Note Context Card (most recent only) -->
        <div v-if="showNoteContext" class="post note-context-post" :key="createKeys.note" :style="{ order: -getOrder('note') }">
          <h3>📝 Create a Note</h3>
          <div class="note-form">
            <div class="form-group">
              <label>What would you like to note?</label>
              <input 
                v-model="noteTitle" 
                type="text" 
                placeholder="Note title..."
                class="note-input"
                @keydown.enter="$refs.noteContent.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me more about it:</label>
              <textarea 
                v-model="noteContent"
                ref="noteContent"
                placeholder="Your thoughts, ideas, or details..."
                rows="4"
                class="note-textarea"
                @keydown.enter.ctrl="saveNote"
              ></textarea>
            </div>
            <!-- Always show Suggested tags (with fallback) -->
            <div class="form-group">
              <label>Suggested tags:</label>
              <div class="suggested-categories suggested-scroll">
                <span v-for="t in (suggestedNoteTags && suggestedNoteTags.length ? suggestedNoteTags : ['ai','answer'])" :key="`sugg2-`+t" class="suggested-tag" @click="addSuggestedNoteTag(t)">#{{ t }}</span>
              </div>
            </div>
            <div class="note-actions">
              <button 
                class="action-button save-note-btn" 
                @click="saveNote" 
                :disabled="!noteTitle.trim() || noteSaving"
              >
                {{ noteSaving ? '💾 Saving...' : '✨ Save Note' }}
              </button>
              <button class="cancel-button" @click="cancelNote">
                Cancel
              </button>
            </div>
            <div class="form-group">
              <label>Tags:</label>
              <TagEditor v-model="noteTags" />
            </div>
          </div>
        </div>

        <!-- Project Context Card (most recent only) -->
        <div v-if="showProjectContext" class="post project-context-post" :key="createKeys.project" :style="{ order: -getOrder('project') }">
          <h3>📁 Create a Project</h3>
          <div class="project-form">
            <div class="form-group">
              <label>What's your project called?</label>
              <input 
                v-model="projectName" 
                type="text" 
                placeholder="Project name..."
                class="project-input"
                @keydown.enter="$refs.projectDescription.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me about this project:</label>
              <textarea 
                v-model="projectDescription"
                ref="projectDescription"
                placeholder="Describe your project goals, timeline, or details..."
                rows="4"
                class="project-textarea"
                @keydown.enter.ctrl="saveProject"
              ></textarea>
            </div>
            <div class="project-actions">
              <button 
                class="action-button save-project-btn" 
                @click="saveProject" 
                :disabled="!projectName.trim() || projectSaving"
              >
                {{ projectSaving ? '💾 Saving...' : '✨ Save Project' }}
              </button>
              <button class="cancel-button" @click="cancelProject">
                Cancel
              </button>
            </div>
            <div class="form-group">
              <label>Tags:</label>
              <TagEditor v-model="projectTags" />
            </div>
          </div>
        </div>

        <!-- Contact Context Card (most recent only) -->
        <div v-if="showContactContext" class="post contact-context-post" :key="createKeys.contact" :style="{ order: -getOrder('contact') }">
          <h3>👤 Add a Contact</h3>
          <div class="contact-form">
            <div class="form-group">
              <label>What's their name?</label>
              <input 
                v-model="contactName" 
                type="text" 
                placeholder="Contact name..."
                class="contact-input"
                @keydown.enter="$refs.contactEmail.focus()"
              />
            </div>
            <div class="form-group">
              <label>Email address:</label>
              <input 
                v-model="contactEmail"
                ref="contactEmail"
                type="email"
                placeholder="their.email@example.com"
                class="contact-input"
                @keydown.enter="$refs.contactPhone.focus()"
              />
            </div>
            <div class="form-group">
              <label>Phone number (optional):</label>
              <input 
                v-model="contactPhone"
                ref="contactPhone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                class="contact-input"
                @keydown.enter.ctrl="saveContact"
              />
            </div>
            <div class="contact-actions">
              <button 
                class="action-button save-contact-btn" 
                @click="saveContact" 
                :disabled="!contactName.trim() || contactSaving"
              >
                {{ contactSaving ? '💾 Saving...' : '✨ Save Contact' }}
              </button>
              <button class="cancel-button" @click="cancelContact">
                Cancel
              </button>
            </div>
            <div class="form-group">
              <label>Tags:</label>
              <TagEditor v-model="contactTags" />
            </div>
          </div>
        </div>

        <!-- Idea Context Card (most recent only) -->
        <div v-if="showIdeaContext" class="post idea-context-post" :key="createKeys.idea" :style="{ order: -getOrder('idea') }">
          <h3>💡 Capture an Idea</h3>
          <div class="idea-form">
            <div class="form-group">
              <label>What's your brilliant idea?</label>
              <input 
                v-model="ideaTitle" 
                type="text" 
                placeholder="Idea title..."
                class="idea-input"
                @keydown.enter="$refs.ideaDescription.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me more about this idea:</label>
              <textarea 
                v-model="ideaDescription"
                ref="ideaDescription"
                placeholder="Describe your idea, how it could work, potential next steps..."
                rows="4"
                class="idea-textarea"
                @keydown.enter.ctrl="saveIdea"
              ></textarea>
            </div>
            <div class="idea-actions">
              <button 
                class="action-button save-idea-btn" 
                @click="saveIdea" 
                :disabled="!ideaTitle.trim() || ideaSaving"
              >
                {{ ideaSaving ? '💾 Saving...' : '✨ Save Idea' }}
              </button>
              <button class="cancel-button" @click="cancelIdea">
                Cancel
              </button>
            </div>
            <div class="form-group">
              <label>Tags:</label>
              <TagEditor v-model="ideaTags" />
            </div>
          </div>
        </div>

        <!-- Profile Setup Post (only during initial setup) -->
        <div v-if="isInitialSetup && showProfileSetup" 
             class="post profile-setup-post" 
             :class="{ 'fade': currentStep > 2 && !isProfileSetupHovered }"
             @mouseenter="handlePostHover(2)"
             @mouseleave="handlePostLeave(2)">
          <h3>📋 Tell us about yourself</h3>
          <div class="setup-form">
            <div class="form-group">
              <label>Full Name:</label>
              <input v-model="profileData.fullName" type="text" placeholder="Enter your full name" />
            </div>
            <div class="form-group">
              <label>Email:</label>
              <input v-model="profileData.email" type="email" placeholder="your.email@example.com" />
            </div>
            <div class="form-group">
              <label>Phone (optional):</label>
              <input v-model="profileData.phone" type="tel" placeholder="+1 (555) 123-4567" />
            </div>
            <button class="action-button" @click="saveProfile" :disabled="!isProfileValid">
              Save Profile
            </button>
          </div>
        </div>

        

        <!-- DYNAMIC POSTS (newest at top, like social media) -->

        <!-- Bulk URL Import Form (dynamic order like other cards) -->
        <div v-if="showBulkImportForm" class="post bulk-import-post" :key="bulkImportKey" :style="{ order: -getOrder('bulk-import') }">
          <h3>📦 Bulk URL Import</h3>
          <p>Paste your URLs below (one per line) and I'll organize them into your webroll with smart tags!</p>
          
          <div class="bulk-import-form">
            <textarea 
              v-model="bulkURLs"
              placeholder="https://example.com&#10;https://github.com/user/repo&#10;https://docs.example.com/guide&#10;..."
              class="bulk-url-input"
              rows="8"
            ></textarea>
            
            <div class="bulk-import-stats" v-if="bulkURLs.trim()">
              <span class="url-count">{{ urlCount }} URLs detected</span>
            </div>
            
            <!-- 🎯 AI Results Preview (Step 5: HITL - Human in the Loop) -->
            <div v-if="bulkImportResults" class="ai-results-preview">
              <h4>🤖 AI Analysis Results</h4>
              <p class="results-summary">{{ bulkImportResults.message }}</p>
              
              <div class="suggested-categories">
                <strong>Suggested Categories:</strong>
                <span v-for="category in bulkImportResults.categories" :key="category" class="suggested-tag">
                  #{{ category }}
                </span>
              </div>
              
              <div class="processed-urls-list">
                <h5>📋 Review & Edit Before Import:</h5>
                <div v-for="(urlData, index) in bulkImportResults.processedUrls" :key="index" class="url-preview-item">
                  <div class="url-preview-header">
                    <span class="url-title">{{ urlData.title }}</span>
                    <span class="ai-badge" v-if="urlData.aiGenerated">🤖 AI</span>
                  </div>
                  <div class="url-preview-url">{{ urlData.url }}</div>
                  <div v-if="urlData.description" class="url-preview-description">{{ urlData.description }}</div>
                  <div class="url-preview-tags">
                    <span v-for="tag in urlData.tags" :key="tag" class="editable-tag">
                      #{{ tag }}
                    </span>
                    <button class="add-tag-btn" @click="addCustomTag(index)" title="Add custom tag">+</button>
                  </div>
                </div>
              </div>
              
              <div class="import-confirmation">
                <button class="action-button confirm-import-btn" @click="confirmAndImport">
                  ✅ Import {{ bulkImportResults.totalUrls }} URLs to Firebase
                </button>
                <button class="cancel-button" @click="editMoreTags">
                  ✏️ Edit More Tags
                </button>
              </div>
            </div>
            
            <!-- 🎉 Import Success Card -->
            <div v-if="bulkImportResults && bulkImportResults.importComplete" class="import-success-card">
              <h4>🎉 Import Complete!</h4>
              <p class="success-message">{{ bulkImportResults.successMessage }}</p>
              
              <div class="import-stats">
                <span class="stat">📦 {{ bulkImportResults.importedCount }} URLs imported</span>
                <span class="stat">🏷️ {{ bulkImportResults.categories.length }} categories suggested</span>
              </div>
              
              <div class="success-actions">
                <button class="action-button" @click="viewWebroll">
                  🔍 View My Webroll
                </button>
                <button class="action-button" @click="askAIAboutImported">
                  🤖 Ask AI About These URLs
                </button>
                <button class="action-button secondary" @click="startNewImport">
                  📦 Import More URLs
                </button>
                <button class="cancel-button" @click="closeImportSession">
                  ✕ Close
                </button>
              </div>
            </div>
            
            <div class="bulk-import-actions" v-if="!bulkImportResults">
              <button 
                class="action-button import-btn" 
                @click="processBulkImport"
                :disabled="!bulkURLs.trim() || bulkImportLoading"
              >
                <span v-if="bulkImportLoading">
                  🔄 Processing... {{ processingProgress }}
                  <span v-if="showCountdown" class="countdown">
                    ⏱️ Expected: {{ expectedTime }}s | Live: {{ liveTime }}s
                    <span v-if="isBeatExpected" class="beating-time">🎉 BEATING EXPECTED!</span>
                  </span>
                </span>
                <span v-else>🎯 Let AI Organize & Import</span>
              </button>
              <button class="cancel-button" @click="cancelBulkImport">
                Cancel
              </button>
            </div>
          </div>
        </div>

        <!-- URL Showcase Post (when url-showcase is most recent) -->
        <div v-if="showURLShowcase" class="post url-showcase-post" :key="showcaseKeys.url" :style="{ order: -getOrder('url-showcase') }">
          <h3>🔗 {{ showcaseCategory === 'webroll' ? '🌐 Webroll' : 'URLs' }} Collection</h3>
          <div class="showcase-header">
            <p><strong>Tag:</strong> #{{ showcaseTag }}</p>
            <p><strong>Found:</strong> {{ showcaseURLs.length }} {{ showcaseURLs.length === 1 ? 'URL' : 'URLs' }}</p>
          </div>
          <div class="url-grid">
            <div v-for="urlItem in showcaseURLs" :key="urlItem.id" class="url-card" @click="inlineEditingId !== urlItem.id ? openURL(urlItem.url, urlItem.title) : null">
              
              <!-- VIEW MODE (default) -->
              <template v-if="inlineEditingId !== urlItem.id">
                <div class="url-header">
                  <div class="url-title-with-logo">
                    <img 
                      v-if="urlItem.logo && urlItem.logo.type === 'favicon'" 
                      :src="urlItem.logo.src" 
                      :alt="urlItem.title" 
                      class="url-logo-image"
                      @error="handleURLLogoError(urlItem)"
                    />
                    <span v-else class="url-logo-emoji">{{ urlItem.logo?.src || '🔗' }}</span>
                    <h4>{{ urlItem.title || urlItem.name || 'Untitled' }}</h4>
                  </div>
                  <div class="url-actions">
                    <button 
                      class="url-edit-btn" 
                      @click.stop="startInlineEdit(urlItem)"
                      title="Edit this URL"
                    >
                      ✏️
                    </button>
                    <button 
                      class="url-delete-btn" 
                      @click.stop="deleteURLInline(urlItem)"
                      title="Delete this URL"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p class="url-link">{{ urlItem.url }}</p>
                <div v-if="urlItem.description" class="url-description">
                  {{ urlItem.description }}
                </div>
                <div class="url-tags" v-if="urlItem.tags">
                  <span v-for="tag in urlItem.tags" :key="tag" class="tag">#{{ tag }}</span>
                </div>
                <div class="url-date">
                  Added {{ formatTime(new Date(urlItem.createdAt)) }}
                </div>
              </template>

              <!-- EDIT MODE -->
              <template v-else>
                <div class="url-header">
                  <div class="url-title-with-logo">
                    <img 
                      v-if="urlItem.logo && urlItem.logo.type === 'favicon'" 
                      :src="urlItem.logo.src" 
                      :alt="urlItem.title" 
                      class="url-logo-image"
                      @error="handleURLLogoError(urlItem)"
                    />
                    <span v-else class="url-logo-emoji">{{ urlItem.logo?.src || '🔗' }}</span>
                    <input 
                      v-model="inlineEditData.title" 
                      class="inline-edit-title"
                      @click.stop
                      placeholder="Enter title..."
                    />
                  </div>
                  <div class="url-actions">
                    <button 
                      class="save-btn-inline" 
                      @click.stop="saveInlineEdit(urlItem)"
                      title="Save changes"
                    >
                      ✅
                    </button>
                    <button 
                      class="cancel-btn-inline" 
                      @click.stop="cancelInlineEdit()"
                      title="Cancel editing"
                    >
                      ❌
                    </button>
                  </div>
                </div>
                <input 
                  v-model="inlineEditData.url" 
                  class="inline-edit-url"
                  @click.stop
                  placeholder="Enter URL..."
                />
                <textarea 
                  v-model="inlineEditData.description" 
                  class="inline-edit-description"
                  @click.stop
                  placeholder="Enter description (optional)..."
                  rows="2"
                ></textarea>
                <div class="inline-edit-tags" @click.stop>
                  <div class="tags-display">
                    <span v-for="(tag, index) in inlineEditData.tags" :key="index" class="tag-editable">
                      #{{ tag }}
                      <button @click="removeTagInline(index)" class="tag-remove">×</button>
                    </span>
                    <button @click="addTagInline()" class="tag-add-btn">+ Add Tag</button>
                  </div>
                </div>
                <div class="url-date">
                  Added {{ formatTime(new Date(urlItem.createdAt)) }}
                </div>
              </template>
              
            </div>
          </div>
          <div class="showcase-actions">
            <button class="action-button" @click="askAIAboutURLs">
              🤖 Ask AI about these URLs
            </button>
            <button class="cancel-button" @click="dismissURLShowcase">
              Close Showcase
            </button>
          </div>
                </div>
        
        <!-- Item Showcase Cards (stacked, newest-first) -->
        <div v-for="card in itemShowcases" :key="card.key" class="post item-showcase-post" :style="{ order: -getOrder('item-showcase-' + card.id) }">
          <h3>{{ getItemIcon(card.category) }} {{ card.pluralName.charAt(0).toUpperCase() + card.pluralName.slice(1) }} Collection</h3>
          <div class="showcase-header">
            <p><strong>Category:</strong> {{ card.pluralName }}</p>
            <p><strong>Found:</strong> {{ knowledgeStore.allItems.filter(i => card.ids.includes(i.id)).length }} {{ knowledgeStore.allItems.filter(i => card.ids.includes(i.id)).length === 1 ? card.category : card.pluralName }}</p>
          </div>
          <div class="item-grid">
            <div v-for="item in knowledgeStore.allItems.filter(i => card.ids.includes(i.id))" :key="item.id" class="item-showcase-card" :class="{ 'note-showcase-card': item.category === 'note' }" @click="inlineEditingItemId !== item.id ? toggleItemExpansion(item.id) : null">
              
              <!-- VIEW MODE -->
              <template v-if="inlineEditingItemId !== item.id">
                <div class="item-showcase-header">
                  <div class="item-showcase-title-with-icon">
                    <span class="item-showcase-icon">{{ getItemIcon(item.category) }}</span>
                    <h4>{{ item.title || item.name || 'Untitled' }}</h4>
                  </div>
                  <div class="item-showcase-actions">
                    <button 
                      class="item-edit-btn" 
                      @click.stop="startItemInlineEdit(item)"
                      title="Edit this item"
                    >
                      ✏️
                    </button>
                    <button 
                      class="item-delete-btn" 
                      @click.stop="deleteItemFromShowcase(item)"
                      title="Delete this item"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </template>
              
              <!-- EDIT MODE -->
              <template v-else>
                <div class="item-showcase-header">
                  <div class="item-showcase-title-with-icon">
                    <span class="item-showcase-icon">{{ getItemIcon(item.category) }}</span>
                    <input 
                      v-model="inlineEditItemData.title" 
                      class="inline-edit-item-title"
                      @click.stop
                      :placeholder="item.category === 'contact' ? 'Enter name...' : 'Enter title...'"
                    />
                  </div>
                  <div class="item-showcase-actions">
                    <button 
                      class="save-btn-inline" 
                      @click.stop="saveItemInlineEdit(item)"
                      title="Save changes"
                    >
                      ✅
                    </button>
                    <button 
                      class="cancel-btn-inline" 
                      @click.stop="cancelItemInlineEdit()"
                      title="Cancel editing"
                    >
                      ❌
                    </button>
                  </div>
                </div>
              </template>
              
              <!-- EDIT MODE CONTENT -->
              <template v-if="inlineEditingItemId === item.id">
                <!-- Contact-specific edit fields -->
                <div v-if="item.category === 'contact'" class="contact-edit-fields">
                  <div class="contact-edit-field">
                    <label>📧 Email:</label>
                    <input 
                      v-model="inlineEditItemData.email" 
                      type="email"
                      class="inline-edit-contact-field"
                      @click.stop
                      placeholder="email@example.com"
                    />
                  </div>
                  <div class="contact-edit-field">
                    <label>📞 Phone:</label>
                    <input 
                      v-model="inlineEditItemData.phone" 
                      type="tel"
                      class="inline-edit-contact-field"
                      @click.stop
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div class="contact-edit-field">
                    <label>📝 Notes:</label>
                    <textarea 
                      v-model="inlineEditItemData.notes" 
                      class="inline-edit-contact-notes"
                      @click.stop
                      placeholder="Additional notes..."
                      rows="2"
            
                    ></textarea>
                    <TagEditor v-model="inlineEditItemData.tags" />
                  </div>
                </div>
                
                <!-- Regular description + tags for non-contacts -->
                <div v-else>
                  <textarea 
                    v-model="inlineEditItemData.description" 
                    class="inline-edit-item-description"
                    @click.stop
                    placeholder="Enter description or content..."
                    rows="3"
                  ></textarea>
                  <TagEditor v-model="inlineEditItemData.tags" />
                </div>
              </template>
              
              <!-- VIEW MODE CONTENT -->
              <template v-else>
                <!-- Contact-specific display -->
              <div v-if="item.category === 'contact'" class="item-showcase-content">
                <p v-if="item.email" class="contact-info">📧 {{ item.email }}</p>
                <p v-if="item.phone" class="contact-info">📞 {{ item.phone }}</p>
                <div v-if="item.notes" class="expandable-content">
                  <p class="item-preview" v-if="!isItemExpanded(item.id) && item.notes.length > 100">
                    {{ item.notes.substring(0, 100) }}...
                    <span class="expand-hint">Click to expand</span>
                  </p>
                  <p class="item-preview" v-else>{{ item.notes }}</p>
                </div>
              </div>
              
              <!-- Regular content display -->
              <div v-else-if="item.description || item.content" class="item-showcase-content">
                <div class="expandable-content">
                  <p class="item-preview" v-if="!isItemExpanded(item.id) && (item.description || item.content).length > 100">
                    {{ (item.description || item.content).substring(0, 100) }}...
                    <span class="expand-hint">Click to expand</span>
                  </p>
                  <p class="item-preview" v-else>{{ item.description || item.content }}</p>
                </div>
              </div>
              
              <!-- Item meta -->
              <div class="item-showcase-meta">
                <span class="item-showcase-date">{{ formatTime(new Date(item.createdAt)) }}</span>
              </div>
              <TagDisplay :tags="item.tags || []" />
              </template>
              
            </div>
          </div>
          <div class="showcase-actions">
            <button class="cancel-button" @click="dismissItemShowcaseCard(card.id)">Close Showcase</button>
          </div>
        </div>
        
        <!-- Selected Item Context Card Post (when item is selected from carousel) -->
        <div v-if="reactiveSelectedItem && showItemContext" class="post item-context-post" :key="itemContextKey" :style="{ order: -getOrder('item-context') }">
          <h3>{{ getItemIcon(reactiveSelectedItem.category) }} {{ getItemTypeDisplay(reactiveSelectedItem.category) }} Details</h3>
          
          <!-- Item Card with Inline Editing -->
          <div class="item-card-context" :class="{ 'note-card': reactiveSelectedItem.category === 'note' }">
            
            <!-- VIEW MODE -->
            <template v-if="inlineEditingItemId !== reactiveSelectedItem.id">
              <div class="item-header">
                <div class="item-title-with-icon">
                  <span class="item-icon">{{ getItemIcon(reactiveSelectedItem.category) }}</span>
                  <h4>{{ reactiveSelectedItem.title || reactiveSelectedItem.name || 'Untitled' }}</h4>
                </div>
                <div class="item-actions">
                  <button 
                    class="item-edit-btn" 
                    @click="startItemInlineEdit(reactiveSelectedItem)"
                    title="Edit this item"
                  >
                    ✏️
                  </button>
                  <button 
                    class="item-delete-btn" 
                    @click="deleteItemInline(reactiveSelectedItem)"
                    title="Delete this item"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <!-- Contact-specific display -->
              <div v-if="reactiveSelectedItem.category === 'contact'" class="contact-details">
                <div v-if="reactiveSelectedItem.email" class="contact-field">
                  <span class="contact-label">📧 Email:</span>
                  <span class="contact-value">{{ reactiveSelectedItem.email }}</span>
                </div>
                <div v-if="reactiveSelectedItem.phone" class="contact-field">
                  <span class="contact-label">📞 Phone:</span>
                  <span class="contact-value">{{ reactiveSelectedItem.phone }}</span>
                </div>
                <div v-if="reactiveSelectedItem.notes" class="contact-field">
                  <span class="contact-label">📝 Notes:</span>
                  <span class="contact-value">{{ reactiveSelectedItem.notes }}</span>
                </div>
              </div>
              
              <!-- Regular description for non-contacts -->
              <div v-else-if="reactiveSelectedItem.description || reactiveSelectedItem.content" class="item-description">
                {{ reactiveSelectedItem.description || reactiveSelectedItem.content }}
              </div>
              
              <div class="item-meta">
                <span class="item-category">Category: {{ reactiveSelectedItem.category }}</span>
                <span class="item-date">Created {{ formatTime(new Date(reactiveSelectedItem.createdAt)) }}</span>
              </div>
              <TagDisplay :tags="reactiveSelectedItem.tags || []" />
            </template>

            <!-- EDIT MODE -->
            <template v-else>
              <div class="item-header">
                <div class="item-title-with-icon">
                  <span class="item-icon">{{ getItemIcon(reactiveSelectedItem.category) }}</span>
                  <input 
                    v-model="inlineEditItemData.title" 
                    class="inline-edit-item-title"
                    @click.stop
                    :placeholder="reactiveSelectedItem.category === 'contact' ? 'Enter name...' : 'Enter title...'"
                  />
                </div>
                <div class="item-actions">
                  <button 
                    class="save-btn-inline" 
                    @click="saveItemInlineEdit(reactiveSelectedItem)"
                    title="Save changes"
                  >
                    ✅
                  </button>
                  <button 
                    class="cancel-btn-inline" 
                    @click="cancelItemInlineEdit()"
                    title="Cancel editing"
                  >
                    ❌
                  </button>
                </div>
              </div>
              
              <!-- Contact-specific edit fields -->
              <div v-if="reactiveSelectedItem.category === 'contact'" class="contact-edit-fields">
                <div class="contact-edit-field">
                  <label>📧 Email:</label>
                  <input 
                    v-model="inlineEditItemData.email" 
                    type="email"
                    class="inline-edit-contact-field"
                    @click.stop
                    placeholder="email@example.com"
                  />
                </div>
                <div class="contact-edit-field">
                  <label>📞 Phone:</label>
                  <input 
                    v-model="inlineEditItemData.phone" 
                    type="tel"
                    class="inline-edit-contact-field"
                    @click.stop
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div class="contact-edit-field">
                  <label>📝 Notes:</label>
                  <textarea 
                    v-model="inlineEditItemData.notes" 
                    class="inline-edit-contact-notes"
                    @click.stop
                    placeholder="Additional notes..."
                    rows="2"
                  ></textarea>
                </div>
                <TagEditor v-model="inlineEditItemData.tags" />
              </div>
              
              <!-- Regular description + tags for non-contacts -->
              <div v-else>
                <textarea 
                  v-model="inlineEditItemData.description" 
                  class="inline-edit-item-description"
                  @click.stop
                  placeholder="Enter description or content..."
                  rows="4"
                ></textarea>
                <TagEditor v-model="inlineEditItemData.tags" />
              </div>
              
              <div class="item-meta">
                <span class="item-category">Category: {{ reactiveSelectedItem.category }}</span>
                <span class="item-date">Created {{ formatTime(new Date(reactiveSelectedItem.createdAt)) }}</span>
              </div>
              
            </template>
            
          </div>
        </div>
        
        <!-- Note Context Card Post (removed lastAction gating; now handled above) -->
        <div v-if="false && showNoteContext" class="post note-context-post">
          <h3>📝 Create a Note</h3>
          <div class="note-form">
            <div class="form-group">
              <label>What would you like to note?</label>
              <input 
                v-model="noteTitle" 
                type="text" 
                placeholder="Note title..."
                class="note-input"
                @keydown.enter="$refs.noteContent.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me more about it:</label>
              <textarea 
                v-model="noteContent"
                ref="noteContent"
                placeholder="Your thoughts, ideas, or details..."
                rows="4"
                class="note-textarea"
                @keydown.enter.ctrl="saveNote"
              ></textarea>
            </div>
            <div class="form-group">
              <label>Suggested tags:</label>
              <div class="suggested-categories suggested-scroll">
                <span v-for="t in (suggestedNoteTags && suggestedNoteTags.length ? suggestedNoteTags : ['ai','answer'])" :key="`sugg-`+t" class="suggested-tag" @click="addSuggestedNoteTag(t)">#{{ t }}</span>
              </div>
            </div>
            <div class="note-actions">
              <button 
                class="action-button save-note-btn" 
                @click="saveNote" 
                :disabled="!noteTitle.trim() || noteSaving"
              >
                {{ noteSaving ? '💾 Saving...' : '✨ Save Note' }}
              </button>
              <button class="cancel-button" @click="cancelNote">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- Project Context Card Post (disabled duplicate) -->
        <div v-if="false && showProjectContext" class="post project-context-post">
          <h3>📁 Create a Project</h3>
          <div class="project-form">
            <div class="form-group">
              <label>What's your project called?</label>
              <input 
                v-model="projectName" 
                type="text" 
                placeholder="Project name..."
                class="project-input"
                @keydown.enter="$refs.projectDescription.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me about this project:</label>
              <textarea 
                v-model="projectDescription"
                ref="projectDescription"
                placeholder="Describe your project goals, timeline, or details..."
                rows="4"
                class="project-textarea"
                @keydown.enter.ctrl="saveProject"
              ></textarea>
            </div>
            <div class="project-actions">
              <button 
                class="action-button save-project-btn" 
                @click="saveProject" 
                :disabled="!projectName.trim() || projectSaving"
              >
                {{ projectSaving ? '💾 Saving...' : '✨ Save Project' }}
              </button>
              <button class="cancel-button" @click="cancelProject">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- Contact Context Card Post (disabled duplicate) -->
        <div v-if="false && showContactContext" class="post contact-context-post">
          <h3>👤 Add a Contact</h3>
          <div class="contact-form">
            <div class="form-group">
              <label>What's their name?</label>
              <input 
                v-model="contactName" 
                type="text" 
                placeholder="Contact name..."
                class="contact-input"
                @keydown.enter="$refs.contactEmail.focus()"
              />
            </div>
            <div class="form-group">
              <label>Email address:</label>
              <input 
                v-model="contactEmail"
                ref="contactEmail"
                type="email"
                placeholder="their.email@example.com"
                class="contact-input"
                @keydown.enter="$refs.contactPhone.focus()"
              />
            </div>
            <div class="form-group">
              <label>Phone number (optional):</label>
              <input 
                v-model="contactPhone"
                ref="contactPhone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                class="contact-input"
                @keydown.enter.ctrl="saveContact"
              />
            </div>
            <div class="contact-actions">
              <button 
                class="action-button save-contact-btn" 
                @click="saveContact" 
                :disabled="!contactName.trim() || contactSaving"
              >
                {{ contactSaving ? '💾 Saving...' : '✨ Save Contact' }}
              </button>
              <button class="cancel-button" @click="cancelContact">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- Idea Context Card Post (disabled duplicate) -->
        <div v-if="false && showIdeaContext" class="post idea-context-post">
          <h3>💡 Capture an Idea</h3>
          <div class="idea-form">
            <div class="form-group">
              <label>What's your brilliant idea?</label>
              <input 
                v-model="ideaTitle" 
                type="text" 
                placeholder="Idea title..."
                class="idea-input"
                @keydown.enter="$refs.ideaDescription.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me more about this idea:</label>
              <textarea 
                v-model="ideaDescription"
                ref="ideaDescription"
                placeholder="Describe your idea, how it could work, potential next steps..."
                rows="4"
                class="idea-textarea"
                @keydown.enter.ctrl="saveIdea"
              ></textarea>
            </div>
            <div class="idea-actions">
              <button 
                class="action-button save-idea-btn" 
                @click="saveIdea" 
                :disabled="!ideaTitle.trim() || ideaSaving"
              >
                {{ ideaSaving ? '💾 Saving...' : '✨ Save Idea' }}
              </button>
              <button class="cancel-button" @click="cancelIdea">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- AI Command Post (disabled duplicate; now handled above) -->
        <div v-if="false && showAICommand" class="post ai-command-post">
          <h3>🤖 AI Assistant</h3>
          <div class="ai-form">
            <div class="form-group ai-input-group">
              <label class="ai-label">Ask AI anything:</label>
              <textarea 
                v-model="aiPrompt" 
                ref="aiTextarea"
                placeholder="Try: 'Create a note about morning routines' or 'Create category Travel' or 'Add https://claude.ai to AI Tools #assistant'"
                rows="6"
                class="ai-textarea"
              ></textarea>
            </div>
            <div class="ai-actions">
              <button class="action-button ai-execute-btn" @click="executeAICommand" :disabled="!aiPrompt.trim() || aiLoading">
                {{ aiLoading ? '🤖 Processing...' : '✨ Execute Command' }}
              </button>
              <button class="cancel-button" @click="hideAIPrompt">
                Cancel
              </button>
            </div>
            <div v-if="aiResponse" class="ai-response">
              <h4>🤖 AI Response:</h4>
              <p>{{ aiResponse }}</p>
            </div>
          </div>
        </div>
        
        <!-- AI Command Post (disabled duplicate) -->
        <div v-if="false && showAICommand" class="post ai-command-post">
          <h3>🤖 AI Assistant</h3>
          <div class="ai-form">
            <div class="form-group ai-input-group">
              <label class="ai-label">Ask AI anything:</label>
              <textarea 
                v-model="aiPrompt" 
                ref="aiTextarea"
                placeholder="Try: 'Create a note about morning routines' or 'Create category Travel' or 'Add https://claude.ai to AI Tools #assistant'"
                rows="6"
                class="ai-textarea"
              ></textarea>
            </div>
            <div class="ai-actions">
              <button class="action-button ai-execute-btn" @click="executeAICommand" :disabled="!aiPrompt.trim() || aiLoading">
                {{ aiLoading ? '🤖 Processing...' : '✨ Execute Command' }}
              </button>
              <button class="cancel-button" @click="hideAIPrompt">
                Cancel
              </button>
            </div>
            <div v-if="aiResponse" class="ai-response">
              <h4>🤖 AI Response:</h4>
              <p>{{ aiResponse }}</p>
            </div>
          </div>
        </div>
        
        <!-- Note Context Card Post (disabled duplicate) -->
        <div v-if="false && showNoteContext" class="post note-context-post">
          <h3>📝 Create a Note</h3>
          <div class="note-form">
            <div class="form-group">
              <label>What would you like to note?</label>
              <input 
                v-model="noteTitle" 
                type="text" 
                placeholder="Note title..."
                class="note-input"
                @keydown.enter="$refs.noteContent.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me more about it:</label>
              <textarea 
                v-model="noteContent"
                ref="noteContent"
                placeholder="Your thoughts, ideas, or details..."
                rows="4"
                class="note-textarea"
                @keydown.enter.ctrl="saveNote"
              ></textarea>
            </div>
            <div class="note-actions">
              <button 
                class="action-button save-note-btn" 
                @click="saveNote" 
                :disabled="!noteTitle.trim() || noteSaving"
              >
                {{ noteSaving ? '💾 Saving...' : '✨ Save Note' }}
              </button>
              <button class="cancel-button" @click="cancelNote">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- 🎉 Bulk Import Success Post (dynamic order) -->
        <div v-if="showImportSuccess" class="post import-success-post" :key="importSuccessKey" :style="{ order: -getOrder('import-success') }">
          <h3>🎉 Bulk Import Complete!</h3>
          <div class="success-content">
            <div class="import-summary">
              <h4>📦 Successfully imported {{ lastImportResults?.importedCount || 0 }} URLs!</h4>
              <p class="success-message">{{ lastImportResults?.successMessage }}</p>
              <p class="processing-time">⚡ Processed in {{ lastImportResults?.processingTime || 'N/A' }}</p>
            </div>
            <div class="import-stats-grid">
              <div class="stat-card">
                <span class="stat-number">{{ lastImportResults?.importedCount || 0 }}</span>
                <span class="stat-label">URLs Imported</span>
              </div>
              <div class="stat-card">
                <span class="stat-number">{{ lastImportResults?.categories?.length || 0 }}</span>
                <span class="stat-label">Categories Suggested</span>
              </div>
            </div>
            <div class="suggested-categories" v-if="lastImportResults?.categories?.length > 0">
              <p><strong>🏷️ Categories:</strong></p>
              <div class="category-tags">
                <span v-for="category in lastImportResults.categories" :key="category" class="category-tag">
                  #{{ category }}
                </span>
              </div>
            </div>
            <div class="success-actions">
              <button class="action-button" @click="viewWebrollFromSuccess">
                🔍 View My Webroll
              </button>
              <button class="action-button" @click="askAIAboutImportedFromSuccess">
                🤖 Ask AI About These URLs
              </button>
              <button class="action-button secondary" @click="startNewImportFromSuccess">
                📦 Import More URLs
              </button>
              <button class="dismiss-button" @click="dismissImportSuccess">
                Got it ✓
              </button>
            </div>
          </div>
        </div>

        <!-- Note Success Post (dynamic order) -->
        <div v-if="showNoteSuccess && lastCreatedNote" class="post note-success-post" :key="successKeys.note" :style="{ order: -getOrder('note-success') }">
          <h3>✅ Note Created Successfully!</h3>
          <div class="success-content">
            <div class="created-note">
              <h4>📝 "{{ lastCreatedNote.title }}"</h4>
              <p v-if="lastCreatedNote.content" class="note-preview">{{ lastCreatedNote.content }}</p>
              <p class="timestamp">Created {{ formatTime(lastCreatedNote.createdAt) }}</p>
            </div>
            <div class="category-status">
              <p>📊 <strong>Notes Collection:</strong> {{ knowledgeStore.notes?.length || 0 }} total notes</p>
              <p>💡 Your note is now available in the main carousel and can be found in your knowledge base.</p>
            </div>
            <div class="success-actions">
              <button class="action-button" @click="createAnotherNote">
                📝 Create Another Note
              </button>
              <button class="dismiss-button" @click="dismissNoteSuccess">
                Got it ✓
              </button>
            </div>
          </div>
        </div>
        
        <!-- Project Success Post (dynamic order) -->
        <div v-if="showProjectSuccess && lastCreatedProject" class="post project-success-post" :key="successKeys.project" :style="{ order: -getOrder('project-success') }">
          <h3>✅ Project Created Successfully!</h3>
          <div class="project-success-content">
            <div class="created-project">
              <h4>📁 "{{ lastCreatedProject.name }}"</h4>
              <p v-if="lastCreatedProject.description" class="project-preview">{{ lastCreatedProject.description }}</p>
              <p class="project-timestamp">Created {{ formatTime(lastCreatedProject.createdAt) }}</p>
            </div>
            <div class="project-category-status">
              <p>📊 <strong>Projects Collection:</strong> {{ knowledgeStore.projects?.length || 0 }} total projects</p>
              <p>💡 Your project is now available in the main carousel and ready for tracking.</p>
            </div>
            <div class="project-success-actions">
              <button class="action-button" @click="createAnotherProject">
                📁 Create Another Project
              </button>
              <button class="cancel-button" @click="dismissProjectSuccess">
                Got it ✓
              </button>
            </div>
          </div>
        </div>
        
        <!-- Project Context Card Post (disabled duplicate) -->
        <div v-if="false && showProjectContext" class="post project-context-post">
          <h3>📁 Create a Project</h3>
          <div class="project-form">
            <div class="form-group">
              <label>What's your project called?</label>
              <input 
                v-model="projectName" 
                type="text" 
                placeholder="Project name..."
                class="project-input"
                @keydown.enter="$refs.projectDescription.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me about this project:</label>
              <textarea 
                v-model="projectDescription"
                ref="projectDescription"
                placeholder="Describe your project goals, timeline, or details..."
                rows="4"
                class="project-textarea"
                @keydown.enter.ctrl="saveProject"
              ></textarea>
            </div>
            <div class="project-actions">
              <button 
                class="action-button save-project-btn" 
                @click="saveProject" 
                :disabled="!projectName.trim() || projectSaving"
              >
                {{ projectSaving ? '💾 Saving...' : '✨ Save Project' }}
              </button>
              <button class="cancel-button" @click="cancelProject">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- Contact Success Post (dynamic order) -->
        <div v-if="showContactSuccess && lastCreatedContact" class="post contact-success-post" :key="successKeys.contact" :style="{ order: -getOrder('contact-success') }">
          <h3>✅ Contact Added Successfully!</h3>
          <div class="contact-success-content">
            <div class="created-contact">
              <h4>👤 "{{ lastCreatedContact.name }}"</h4>
              <p v-if="lastCreatedContact.email" class="contact-preview">📧 {{ lastCreatedContact.email }}</p>
              <p v-if="lastCreatedContact.phone" class="contact-preview">📞 {{ lastCreatedContact.phone }}</p>
              <p class="contact-timestamp">Added {{ formatTime(lastCreatedContact.createdAt) }}</p>
            </div>
            <div class="contact-category-status">
              <p>📊 <strong>Contacts Collection:</strong> {{ knowledgeStore.contacts?.length || 0 }} total contacts</p>
              <p>💡 Your contact is now available in the main carousel and ready for quick access.</p>
            </div>
            <div class="contact-success-actions">
              <button class="action-button" @click="createAnotherContact">
                👤 Add Another Contact
              </button>
              <button class="cancel-button" @click="dismissContactSuccess">
                Got it ✓
              </button>
            </div>
          </div>
        </div>
        
        <!-- Contact Context Card Post (disabled duplicate) -->
        <div v-if="false && showContactContext" class="post contact-context-post">
          <h3>👤 Add a Contact</h3>
          <div class="contact-form">
            <div class="form-group">
              <label>What's their name?</label>
              <input 
                v-model="contactName" 
                type="text" 
                placeholder="Contact name..."
                class="contact-input"
                @keydown.enter="$refs.contactEmail.focus()"
              />
            </div>
            <div class="form-group">
              <label>Email address:</label>
              <input 
                v-model="contactEmail"
                ref="contactEmail"
                type="email"
                placeholder="their.email@example.com"
                class="contact-input"
                @keydown.enter="$refs.contactPhone.focus()"
              />
            </div>
            <div class="form-group">
              <label>Phone number (optional):</label>
              <input 
                v-model="contactPhone"
                ref="contactPhone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                class="contact-input"
                @keydown.enter.ctrl="saveContact"
              />
            </div>
            <div class="contact-actions">
              <button 
                class="action-button save-contact-btn" 
                @click="saveContact" 
                :disabled="!contactName.trim() || contactSaving"
              >
                {{ contactSaving ? '💾 Saving...' : '✨ Save Contact' }}
              </button>
              <button class="cancel-button" @click="cancelContact">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- Idea Success Post (dynamic order) -->
        <div v-if="showIdeaSuccess && lastCreatedIdea" class="post idea-success-post" :key="successKeys.idea" :style="{ order: -getOrder('idea-success') }">
          <h3>✅ Idea Captured Successfully!</h3>
          <div class="idea-success-content">
            <div class="created-idea">
              <h4>💡 "{{ lastCreatedIdea.title }}"</h4>
              <p v-if="lastCreatedIdea.description" class="idea-preview">{{ lastCreatedIdea.description }}</p>
              <p class="idea-timestamp">Captured {{ formatTime(lastCreatedIdea.createdAt) }}</p>
            </div>
            <div class="idea-category-status">
              <p>📊 <strong>Ideas Collection:</strong> {{ knowledgeStore.ideas?.length || 0 }} total ideas</p>
              <p>💡 Your idea is now available in the main carousel and ready to be developed.</p>
            </div>
            <div class="idea-success-actions">
              <button class="action-button" @click="createAnotherIdea">
                💡 Capture Another Idea
              </button>
              <button class="cancel-button" @click="dismissIdeaSuccess">
                Got it ✓
              </button>
            </div>
          </div>
        </div>
        
        <!-- Idea Context Card Post (disabled duplicate) -->
        <div v-if="false && showIdeaContext" class="post idea-context-post">
          <h3>💡 Capture an Idea</h3>
          <div class="idea-form">
            <div class="form-group">
              <label>What's your brilliant idea?</label>
              <input 
                v-model="ideaTitle" 
                type="text" 
                placeholder="Idea title..."
                class="idea-input"
                @keydown.enter="$refs.ideaDescription.focus()"
              />
            </div>
            <div class="form-group">
              <label>Tell me more about this idea:</label>
              <textarea 
                v-model="ideaDescription"
                ref="ideaDescription"
                placeholder="Describe your idea, how it could work, potential next steps..."
                rows="4"
                class="idea-textarea"
                @keydown.enter.ctrl="saveIdea"
              ></textarea>
            </div>
            <div class="idea-actions">
              <button 
                class="action-button save-idea-btn" 
                @click="saveIdea" 
                :disabled="!ideaTitle.trim() || ideaSaving"
              >
                {{ ideaSaving ? '💾 Saving...' : '✨ Save Idea' }}
              </button>
              <button class="cancel-button" @click="cancelIdea">
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- Getting Started Post (moves down when other posts appear) -->
        <div v-if="!isInitialSetup" class="post getting-started-post">
          <h3>👋 What would you like to do?</h3>
          <p>Use the command bar at the bottom to get started:</p>
          <div class="command-guide">
            <div class="command-item">
              <span class="guide-icon">📝</span>
              <div class="guide-text">
                <strong>Create Note</strong>
                <p>Click to add notes, thoughts, or ideas</p>
              </div>
            </div>
            <div class="command-item">
              <span class="guide-icon">📁</span>
              <div class="guide-text">
                <strong>Create Project</strong>
                <p>Start organizing your projects</p>
              </div>
            </div>
            <div class="command-item">
              <span class="guide-icon">👤</span>
              <div class="guide-text">
                <strong>Add Contact</strong>
                <p>Save important contacts</p>
              </div>
            </div>
            <div class="command-item">
              <span class="guide-icon">💡</span>
              <div class="guide-text">
                <strong>Save Idea</strong>
                <p>Capture brilliant ideas</p>
              </div>
            </div>
            <div class="command-item">
              <span class="guide-icon">🤖</span>
              <div class="guide-text">
                <strong>Ask AI</strong>
                <p>Make your AI Assistant do it for you!</p>
              </div>
            </div>
          </div>
          <div class="getting-started-footer">
            <p>💡 <strong>Tip:</strong> All your actions will appear here as a feed - newest first!</p>
          </div>
        </div>

        <!-- Setup Complete Post (only during initial setup) -->
        <div v-if="isInitialSetup && showSetupComplete" 
             class="post setup-complete-post">
          <h3>🎉 Setup Complete!</h3>
          <p>Your Private App is ready to use.</p>
          <p>You can now:</p>
          <ul>
            <li>📝 Create and manage notes</li>
            <li>📁 Organize projects</li>
            <li>🤖 Use AI assistance</li>
            <li>📚 Build your knowledge base</li>
          </ul>
          <button class="action-button" @click="finishSetup">
            Start Using Private App
          </button>
        </div>

        <!-- In-App Webview -->
        <div v-if="showWebview" class="webview-overlay">
          <div class="webview-container">
            <div class="webview-header">
              <div class="webview-title">
                <span class="webview-icon">🌐</span>
                <span>{{ webviewTitle }}</span>
              </div>
              <div class="webview-controls">
                <button 
                  v-if="!webviewError && !webviewLoading"
                  class="webview-fix-btn" 
                  @click="markSiteAsBroken" 
                  title="If the website isn't displaying properly, click here to open it externally"
                >
                  ⚠️ Site not loading?
                </button>
                <button class="webview-refresh-btn" @click="onWebviewLoad" title="Refresh">
                  🔄
                </button>
                <button class="webview-close-btn" @click="closeWebview" title="Close">
                  ✕
                </button>
              </div>
            </div>
            <div class="webview-content">
              <div v-if="webviewLoading" class="webview-loading">
                <div class="loading-spinner"></div>
                <p>Loading {{ webviewTitle }}...</p>
              </div>
              
              <div v-else-if="webviewError" class="webview-error">
                <div class="error-icon">🚫</div>
                <h3>Cannot Display in App</h3>
                <p>{{ webviewTitle }} refuses to load in embedded view for security reasons.</p>
                <div class="error-actions">
                  <button class="action-button" @click="openInNewTab">
                    🪟 Open in Popup Window
                  </button>
                  <button class="action-button" @click="askAIAboutSite">
                    🤖 Ask AI About Site
                  </button>
                  <button class="cancel-button" @click="closeWebview">
                    ← Back to Showcase
                  </button>
                </div>
              </div>
              
              <iframe 
                v-if="!webviewLoading && !webviewError"
                ref="webviewIframe"
                :src="webviewURL"
                @load="onWebviewLoad"
                @error="onWebviewError"
                frameborder="0"
                class="webview-frame"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              ></iframe>
            </div>
          </div>
        </div>
        
        <!-- TODO: Add Private App specific content here -->
      </div>
      
      <!-- Sticky Command Bar (outside posts-container) -->
      <div v-if="!isInitialSetup" class="sticky-command-bar">
        <div class="command-options">
          <button class="command-btn" @click="addNote" title="Create Note">
            📝
          </button>
          <button class="command-btn" @click="addProject" title="Create Project">
            📁
          </button>
          <button class="command-btn" @click="addContact" title="Add Contact">
            👤
          </button>
          <button class="command-btn" @click="addIdea" title="Save Idea">
            💡
          </button>
          <button class="command-btn ai-btn" @click="showAIPrompt" title="Ask AI">
            🤖
          </button>
        </div>
      </div>
      
      <button class="close-button" @click="close">✕</button>
    </div>
    
    <!-- Legacy URL Edit Modal - REMOVED (replaced by inline editing) -->
  </div>
</template>

<script>
import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import { useRolesStore } from '../stores/roles';
import { useKnowledgeStore } from '../stores/knowledge';
import { geminiService } from '../services/geminiService';
import { logoService } from '../services/logoService';
import TagDisplay from './tags/TagDisplay.vue';
import TagEditor from './tags/TagEditor.vue';


export default {
  name: 'CommandFeedModal',
  components: {
    // TODO: Add KB/Private App specific components
    TagDisplay,
    TagEditor
  },
  props: {
    user: {
      type: Object,
      default: null
    },
    isInitialSetup: {
      type: Boolean,
      default: true
    },
    selectedURLItem: {
      type: Object,
      default: null
    },
    selectedItem: {
      type: Object,
      default: null
    }
  },
  setup() {
    const rolesStore = useRolesStore();
    const knowledgeStore = useKnowledgeStore();
    
    return {
      rolesStore,
      knowledgeStore
    };
  },
  data() {
    return {
      // Tag chooser state
      showTagChooser: false,
      tagChooserKey: 0,
      tagChooserCandidates: [],
      tagChooserAction: null, // { action: 'add'|'remove', tag, category?, title? }
      // Tag op success state
      showTagOpSuccess: false,
      tagOpSuccessData: null, // { action, tag, item, previousTags }
      tagOpSuccessKey: 0,
      // AI card state
      aiCardDisabled: false,
      // AI answer state
      showAIAnswer: false,
      aiAnswerText: '',
      aiAnswerKey: 0,
      // Keep last asked prompt for quick-fill
      lastAIPrompt: '',
      // Suggested tags for quick-fill note
      suggestedNoteTags: [],
      showSuggestedNoteTags: false,
      currentStep: 1,
      feedOrderCounter: 0,
      feedOrderTokens: new Map(),
      // Dynamic feed system (newest-first)
      feedItems: [],
      feedIdCounter: 0,
      isWelcomePostHovered: false,
      isProfileSetupHovered: false,
      hoveredPost: null,
      setupComplete: false,
      showProfileSetup: false,
      showSetupComplete: false,
      profileData: {
        fullName: '',
        email: '',
        phone: ''
      },
      // AI command state
      showAICommand: false,
      aiPrompt: '',
      aiResponse: '',
      aiLoading: false,
      // Note context card state
      showNoteContext: false,
      noteTitle: '',
      noteContent: '',
      noteTags: [],
      noteSaving: false,
      // Note success state
      showNoteSuccess: false,
      lastCreatedNote: null,
      // Project context card state
      showProjectContext: false,
      projectName: '',
      projectDescription: '',
      projectTags: [],
      projectSaving: false,
      // Project success state
      showProjectSuccess: false,
      lastCreatedProject: null,
      // Contact context card state
      showContactContext: false,
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      contactTags: [],
      contactSaving: false,
      // Contact success state
      showContactSuccess: false,
      lastCreatedContact: null,
      // Idea context card state
      showIdeaContext: false,
      ideaTitle: '',
      ideaDescription: '',
      ideaTags: [],
      ideaSaving: false,
      // Idea success state
      showIdeaSuccess: false,
      lastCreatedIdea: null,
      // URL Showcase state (for AI-powered URL display)
      showURLShowcase: false,
      showcaseURLsFilter: null, // Filter criteria for reactive showcase
      showcaseTag: '',
      showcaseCategory: '',
      
      // Item Showcase state (for Notes, Projects, Contacts, Ideas)
      showItemShowcase: false,
      showcaseItemsFilter: null, // Filter criteria for item showcase
      showcaseItemCategory: '',
      showcaseItemPluralName: '',
      // New: multiple stacked item showcases
      itemShowcases: [], // [{ id, key, category, pluralName, ids: string[] }]
      itemShowcaseIdCounter: 0,
      expandedItemIds: new Set(), // Track which items are expanded
      internalSelectedItem: null, // Internal selected item for showcase editing
      // Selected item context card state
      showItemContext: false,
      itemContextKey: 0,
      
      // Webview state (for in-app browsing)
      showWebview: false,
      webviewURL: '',
      webviewTitle: '',
      webviewLoading: false,
      webviewError: false,
      // Error listener references to clean up
      cspViolationHandler: null,
      originalWindowError: undefined,
      originalConsoleError: null,
      errorCheckTimeout: null,
      // Broken sites blocklist (user-reported)
      brokenSitesList: this.loadBrokenSitesList(),
      
              // Legacy URL Edit Modal state - REMOVED

        // Inline editing state
        inlineEditingId: null, // ID of the URL being edited inline
        inlineEditData: {}, // Temporary edit data
        
        // Item inline editing state
        inlineEditingItemId: null, // ID of the item (note/project/contact/idea) being edited
        inlineEditItemData: {}, // Temporary item edit data
      
      // Bulk URL import state
      showBulkImportForm: false,
      bulkURLs: '',
      bulkImportLoading: false,
      bulkImportResults: null,
      bulkImportKey: 0,
      // UI progress fields for bulk import (avoid undefined warnings)
      processingProgress: '',
      expectedTime: 0,
      liveTime: 0,
      showCountdown: false,
      isBeatExpected: false,
      // Import success state
      showImportSuccess: false,
      lastImportResults: null,
      importSuccessKey: 0,

      // Success keys for dynamic remounting
      successKeys: { note: 0, project: 0, contact: 0, idea: 0 },
      importStartTime: null,
      importSuccessKey: 0,
      
      // Track last action for proper ordering
      lastAction: null, // 'ai', 'note', 'project', 'contact', 'idea', 'url-showcase', 'bulk-import', etc.
      
      // URL logo cache for feed cards
      urlLogos: new Map(),
      // Keys to force remount for showcases on re-open
      showcaseKeys: { item: 0, url: 0 },
      // Keys to force remount for creation cards on re-open
      createKeys: { note: 0, project: 0, contact: 0, idea: 0 },
      // Key to force remount for AI card on re-open
      aiKey: 0,
      // Tag filter bar state
      
    }
  },
  computed: {
    // Sort feed items by newest first
    sortedFeedItems() {
      return this.feedItems
        .slice()
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    },
    getOrder() {
      // return a function for style binding
      return (key) => this.feedOrderTokens.get(key) ?? 0;
    },
    userRole() {
      return this.rolesStore.userRole;
    },
    roleDisplayName() {
      return this.rolesStore.availableRoles[this.userRole]?.name || 'User';
    },
    
    // Reactive showcase URLs that update with Firebase changes
    showcaseURLs() {
      if (!this.showcaseURLsFilter) return [];
      
      const allItems = this.knowledgeStore.allItems;
      return allItems.filter(item => this.showcaseURLsFilter(item));
    },
    
    showcaseItems() {
      if (!this.showcaseItemsFilter) return [];
      
      const allItems = this.knowledgeStore.allItems;
      return allItems.filter(item => this.showcaseItemsFilter(item));
    },
    
    // Reactive selected item that updates with Firebase changes
    reactiveSelectedItem() {
      // Use internal selected item if available (from showcase), otherwise use prop
      const selectedItem = this.internalSelectedItem || this.selectedItem;
      if (!selectedItem) return null;
      
      const allItems = this.knowledgeStore.allItems;
      return allItems.find(item => item.id === selectedItem.id) || selectedItem;
    },
    isProfileValid() {
      return this.profileData.fullName && this.profileData.email;
    },
    urlCount() {
      if (!this.bulkURLs.trim()) return 0;
      const urls = this.bulkURLs.split('\n').filter(line => {
        const trimmed = line.trim();
        return trimmed && (trimmed.startsWith('http://') || trimmed.startsWith('https://'));
      });
      return urls.length;
    }
  },
  watch: {
    showAICommand(val) {
      if (val) this.bumpOrder('ai');
    },
    showItemShowcase(val) {
      if (val) this.bumpOrder('item-showcase');
    },
    showURLShowcase(val) {
      if (val) this.bumpOrder('url-showcase');
    },
    showItemContext(val) {
      if (val) this.bumpOrder('item-context');
    },
    showNoteContext(val) {
      if (val) {
        this.bumpOrder('note');
        // Ensure suggested tags always appear when opening the note form
        this.computeSuggestedNoteTags();
      }
    },
    showProjectContext(val) {
      if (val) this.bumpOrder('project');
    },
    showContactContext(val) {
      if (val) this.bumpOrder('contact');
    },
    showIdeaContext(val) {
      if (val) this.bumpOrder('idea');
    },
    showNoteSuccess(val) {
      if (val) { this.bumpOrder('note-success'); this.successKeys.note++; }
    },
    showProjectSuccess(val) {
      if (val) { this.bumpOrder('project-success'); this.successKeys.project++; }
    },
    showContactSuccess(val) {
      if (val) { this.bumpOrder('contact-success'); this.successKeys.contact++; }
    },
    showIdeaSuccess(val) {
      if (val) { this.bumpOrder('idea-success'); this.successKeys.idea++; }
    }
  },
  methods: {
    dismissItemShowcaseCard(cardId) {
      this.itemShowcases = this.itemShowcases.filter(c => c.id !== cardId);
    },
    // Add a new item to the dynamic feed (does not modify legacy lastAction)
    addToFeed(type, category, showFlag, data = {}) {
      this.feedItems.unshift({
        id: ++this.feedIdCounter,
        type,
        category,
        showFlag,
        timestamp: Date.now(),
        data
      });
    },
    bumpOrder(key) {
      this.feedOrderTokens.set(key, ++this.feedOrderCounter);
    },
    handlePostHover(postNumber) {
      if (postNumber === 1) this.isWelcomePostHovered = true;
      if (postNumber === 2) this.isProfileSetupHovered = true;
      this.hoveredPost = postNumber;
    },
    handlePostLeave(postNumber) {
      if (postNumber === 1) this.isWelcomePostHovered = false;
      if (postNumber === 2) this.isProfileSetupHovered = false;
      this.hoveredPost = null;
    },
    close() {
      // Restore background scrolling
      document.body.style.overflow = '';
      this.$emit('close');
    },
    startProfileSetup() {
      this.currentStep = 2;
      this.showProfileSetup = true;
    },
    async saveProfile() {
      // Save profile using knowledge store
      try {
        if (this.user?.uid) {
          await this.knowledgeStore.saveUserProfile(this.profileData);
          console.log('Profile saved to Firebase:', this.profileData);
        }
        
        this.showProfileSetup = false;
        this.showSetupComplete = true;
      } catch (error) {
        console.error('Error saving profile:', error);
        alert('Error saving profile. Please try again.');
      }
    },
    finishSetup() {
      this.currentStep = 3; // Assuming 3 is the final step
      this.showSetupComplete = false;
      // You might want to emit an event or update a prop to indicate setup is done
      this.$emit('setup-complete');
    },
    // Add item methods (now with Firebase)
    async addNote() {
      // Show note context card on top (don't override lastAction to preserve showcase)
      this.showNoteContext = true;
      this.noteTitle = '';
      this.noteContent = '';
      // Don't change lastAction - preserve showcase session
      // Re-open behavior: bring to top and remount
      this.bumpOrder('note');
      this.createKeys.note++;
    },
    
    createAnotherNote() {
      // Dismiss success post first, then show new context card
      this.showNoteSuccess = false;
      this.lastCreatedNote = null;
      // Show new note context card (which will appear at top)
      this.lastAction = 'note';
      this.showNoteContext = true;
      this.noteTitle = '';
      this.noteContent = '';
      // New: push to dynamic feed
      this.addToFeed('creation', 'note', 'showNoteContext');
    },
    
    async saveNote() {
      if (!this.noteTitle.trim()) return;
      
      this.noteSaving = true;
      try {
        await this.knowledgeStore.addNote({ 
          title: this.noteTitle, 
          content: this.noteContent,
          tags: this.noteTags
        });
        console.log('Note created:', this.noteTitle);
        
        // Store the created note info and show success
        this.lastCreatedNote = {
          title: this.noteTitle,
          content: this.noteContent,
          createdAt: new Date()
        };
        
        // Hide context card and show success
        this.showNoteContext = false;
        this.showNoteSuccess = true;
        
        // Clear form data
        this.noteTitle = '';
        this.noteContent = '';
        this.noteTags = [];
        // New: push success to dynamic feed and bump order
        this.addToFeed('success', 'note', 'showNoteSuccess', { note: this.lastCreatedNote });
        this.bumpOrder('note-success');
        this.successKeys.note++;
      } catch (error) {
        console.error('Error creating note:', error);
        alert('Error creating note. Please try again.');
      } finally {
        this.noteSaving = false;
      }
    },
    
    cancelNote() {
      this.showNoteContext = false;
      this.noteTitle = '';
      this.noteContent = '';
    },
    
    dismissNoteSuccess() {
      this.showNoteSuccess = false;
      this.lastCreatedNote = null;
    },
    
    formatTime(date) {
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      
      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
      return date.toLocaleDateString();
    },
    addProject() {
      // Show project context card on top (don't override lastAction to preserve showcase)
      this.showProjectContext = true;
      this.projectName = '';
      this.projectDescription = '';
      this.projectTags = [];
      // Don't change lastAction - preserve showcase session
      // New: push to dynamic feed & re-open behavior
      this.addToFeed('creation', 'project', 'showProjectContext');
      this.bumpOrder('project');
      this.createKeys.project++;
    },
    
    async saveProject() {
      if (!this.projectName.trim()) return;
      
      this.projectSaving = true;
      try {
        await this.knowledgeStore.addProject({ 
          name: this.projectName, 
          description: this.projectDescription,
          tags: this.projectTags
        });
        console.log('Project created:', this.projectName);
        
        // Store the created project info and show success
        this.lastCreatedProject = {
          name: this.projectName,
          description: this.projectDescription,
          createdAt: new Date()
        };
        
        // Hide context card and show success
        this.showProjectContext = false;
        this.showProjectSuccess = true;
        
        // Clear form data
        this.projectName = '';
        this.projectDescription = '';
        this.projectTags = [];
        // New: push success to dynamic feed and bump order
        this.addToFeed('success', 'project', 'showProjectSuccess', { project: this.lastCreatedProject });
        this.bumpOrder('project-success');
        this.successKeys.project++;
      } catch (error) {
        console.error('Error creating project:', error);
        alert('Error creating project. Please try again.');
      } finally {
        this.projectSaving = false;
      }
    },
    
    cancelProject() {
      this.showProjectContext = false;
      this.projectName = '';
      this.projectDescription = '';
    },
    
    createAnotherProject() {
      // Dismiss success post first, then show new context card
      this.showProjectSuccess = false;
      this.lastCreatedProject = null;
      // Show new project context card (which will appear at top)
      this.lastAction = 'project';
      this.showProjectContext = true;
      this.projectName = '';
      this.projectDescription = '';
    },
    
    dismissProjectSuccess() {
      this.showProjectSuccess = false;
      this.lastCreatedProject = null;
    },
    
    addContact() {
      // Show contact context card on top (don't override lastAction to preserve showcase)
      this.showContactContext = true;
      this.contactName = '';
      this.contactEmail = '';
      this.contactPhone = '';
      this.contactTags = [];
      // Don't change lastAction - preserve showcase session
      // New: push to dynamic feed & re-open behavior
      this.addToFeed('creation', 'contact', 'showContactContext');
      this.bumpOrder('contact');
      this.createKeys.contact++;
    },
    
    async saveContact() {
      if (!this.contactName.trim()) return;
      
      this.contactSaving = true;
      try {
        await this.knowledgeStore.addContact({ 
          name: this.contactName, 
          email: this.contactEmail,
          phone: this.contactPhone,
          tags: this.contactTags
        });
        console.log('Contact created:', this.contactName);
        
        // Store the created contact info and show success
        this.lastCreatedContact = {
          name: this.contactName,
          email: this.contactEmail,
          phone: this.contactPhone,
          createdAt: new Date()
        };
        
        // Hide context card and show success
        this.showContactContext = false;
        this.showContactSuccess = true;
        
        // Clear form data
        this.contactName = '';
        this.contactEmail = '';
        this.contactPhone = '';
        this.contactTags = [];
        // New: push success to dynamic feed and bump order
        this.addToFeed('success', 'contact', 'showContactSuccess', { contact: this.lastCreatedContact });
        this.bumpOrder('contact-success');
        this.successKeys.contact++;
      } catch (error) {
        console.error('Error creating contact:', error);
        alert('Error creating contact. Please try again.');
      } finally {
        this.contactSaving = false;
      }
    },
    
    cancelContact() {
      this.showContactContext = false;
      this.contactName = '';
      this.contactEmail = '';
      this.contactPhone = '';
    },
    
    createAnotherContact() {
      // Dismiss success post first, then show new context card
      this.showContactSuccess = false;
      this.lastCreatedContact = null;
      // Show new contact context card (which will appear at top)
      this.lastAction = 'contact';
      this.showContactContext = true;
      this.contactName = '';
      this.contactEmail = '';
      this.contactPhone = '';
    },
    
    dismissContactSuccess() {
      this.showContactSuccess = false;
      this.lastCreatedContact = null;
    },
    
    addIdea() {
      // Show idea context card on top (don't override lastAction to preserve showcase)
      this.showIdeaContext = true;
      this.ideaTitle = '';
      this.ideaDescription = '';
      this.ideaTags = [];
      // Don't change lastAction - preserve showcase session
      // New: push to dynamic feed & re-open behavior
      this.addToFeed('creation', 'idea', 'showIdeaContext');
      this.bumpOrder('idea');
      this.createKeys.idea++;
    },
    
    async saveIdea() {
      if (!this.ideaTitle.trim()) return;
      
      this.ideaSaving = true;
      try {
        await this.knowledgeStore.addIdea({ 
          title: this.ideaTitle, 
          description: this.ideaDescription,
          tags: this.ideaTags
        });
        console.log('Idea created:', this.ideaTitle);
        
        // Store the created idea info and show success
        this.lastCreatedIdea = {
          title: this.ideaTitle,
          description: this.ideaDescription,
          createdAt: new Date()
        };
        
        // Hide context card and show success
        this.showIdeaContext = false;
        this.showIdeaSuccess = true;
        
        // Clear form data
        this.ideaTitle = '';
        this.ideaDescription = '';
        this.ideaTags = [];
        // New: push success to dynamic feed and bump order
        this.addToFeed('success', 'idea', 'showIdeaSuccess', { idea: this.lastCreatedIdea });
        this.bumpOrder('idea-success');
        this.successKeys.idea++;
      } catch (error) {
        console.error('Error creating idea:', error);
        alert('Error creating idea. Please try again.');
      } finally {
        this.ideaSaving = false;
      }
    },
    
    cancelIdea() {
      this.showIdeaContext = false;
      this.ideaTitle = '';
      this.ideaDescription = '';
    },
    
    createAnotherIdea() {
      // Dismiss success post first, then show new context card
      this.showIdeaSuccess = false;
      this.lastCreatedIdea = null;
      // Show new idea context card (which will appear at top)
      this.lastAction = 'idea';
      this.showIdeaContext = true;
      this.ideaTitle = '';
      this.ideaDescription = '';
    },
    
    dismissIdeaSuccess() {
      this.showIdeaSuccess = false;
      this.lastCreatedIdea = null;
    },
    
    // AI Command methods
    showAIPrompt() {
      // Show AI command on top (don't override lastAction to preserve showcase)
      this.showAICommand = true;
      this.aiPrompt = '';
      this.aiResponse = '';
      // Don't change lastAction - preserve showcase session
      // Re-open behavior: bring to top and remount
      this.bumpOrder('ai');
      this.aiKey++;
    },
    
    hideAIPrompt() {
      this.showAICommand = false;
      this.aiPrompt = '';
      this.aiResponse = '';
    },
    
    async executeAICommand() {
      if (!this.aiPrompt.trim()) return;
      
      this.aiLoading = true;
      this.aiResponse = '';
      
      try {
        console.log('🤖 Executing AI command:', this.aiPrompt);
        
        // Check if this is a URL showcase command with smart pattern matching
        const command = this.aiPrompt.toLowerCase().trim();
        this.lastAIPrompt = this.aiPrompt;
        console.log('🔍 DEBUG: Processing command:', command);
        
        // 🧠 Smart AI understanding - like you! Multiple patterns for natural language
        const webrollPatterns = [
          /\b(view|show|see|display|list|get|open)\b.*\b(webroll|web\s*roll|urls?|links?|bookmarks?|saved\s*sites?)\b/i,
          /\bmy\s+(webroll|web\s*roll|urls?|links?|bookmarks?|saved\s*sites?)\b/i,
          /\b(webroll|web\s*roll)\b/i,
          /\bshow.*\b(saved|collected|imported|stored)\b.*\b(urls?|links?|sites?)\b/i,
          /\bwhat\s+(urls?|links?|sites?)\s+do\s+i\s+have/i,
          /\blist\s+my\s+(urls?|links?|sites?|bookmarks?)/i
        ];
        // 🏷️ Tag management patterns
        // Accept straight or curly quotes, or unquoted titles to EOL
        const tagAddPattern = /(add|apply)\s+tag\s+#([\w-]+)\s+to\s+(note|project|contact|idea|url|link)\s+(?:['\"“”‘’]([^'\"“”‘’]+)['\"“”‘’]|(.+))\s*$/i;
        const tagRemovePattern = /(remove|delete)\s+tag\s+#([\w-]+)\s+from\s+(note|project|contact|idea|url|link)\s+(?:['\"“”‘’]([^'\"“”‘’]+)['\"“”‘’]|(.+))\s*$/i;
        const tagAddHerePattern = /(add|apply)\s+tag\s+#([\w-]+)\s+(here|to this)/i;
        const tagRemoveHerePattern = /(remove|delete)\s+tag\s+#([\w-]+)\s+(here|from this)/i;
        const tagRenamePattern = /rename\s+tag\s+#([\w-]+)\s+to\s+#([\w-]+)(?:\s+in\s+(notes|projects|contacts|ideas|webroll))?/i;
        
        // 📝 Notes patterns
        const notesPatterns = [
          /\b(view|show|see|display|list|get|open)\b.*\b(notes?)\b/i,
          /\bmy\s+(notes?)\b/i,
          /\b(notes?)\b/i,
          /\bshow.*\b(saved|written|created)\b.*\b(notes?)\b/i,
          /\bwhat\s+(notes?)\s+do\s+i\s+have/i,
          /\blist\s+my\s+(notes?)/i
        ];
        
        // 📁 Projects patterns  
        const projectsPatterns = [
          /\b(view|show|see|display|list|get|open)\b.*\b(projects?)\b/i,
          /\bmy\s+(projects?)\b/i,
          /\b(projects?)\b/i,
          /\bshow.*\b(active|current|ongoing)\b.*\b(projects?)\b/i,
          /\bwhat\s+(projects?)\s+do\s+i\s+have/i,
          /\blist\s+my\s+(projects?)/i
        ];
        
        // 👤 Contacts patterns
        const contactsPatterns = [
          /\b(view|show|see|display|list|get|open)\b.*\b(contacts?|people)\b/i,
          /\bmy\s+(contacts?|people)\b/i,
          /\b(contacts?)\b/i,
          /\bshow.*\b(saved|added)\b.*\b(contacts?|people)\b/i,
          /\bwhat\s+(contacts?|people)\s+do\s+i\s+have/i,
          /\blist\s+my\s+(contacts?|people)/i
        ];
        
        // 💡 Ideas patterns
        const ideasPatterns = [
          /\b(view|show|see|display|list|get|open)\b.*\b(ideas?)\b/i,
          /\bmy\s+(ideas?)\b/i,
          /\b(ideas?)\b/i,
          /\bshow.*\b(saved|captured|noted)\b.*\b(ideas?)\b/i,
          /\bwhat\s+(ideas?)\s+do\s+i\s+have/i,
          /\blist\s+my\s+(ideas?)/i
        ];
        
        const isWebrollCommand = webrollPatterns.some(pattern => pattern.test(command));
        // Tag queries: "#tag" or "my #tag <category>"
        const tagOnlyMatch = command.match(/^#([\w-]+)$/i);
        const tagWithCategoryMatch = command.match(/^my\s+#([\w-]+)\s+(notes?|projects?|contacts?|ideas?|urls?|links?|webroll)$/i);
        const isNotesCommand = notesPatterns.some(pattern => pattern.test(command));
        const isProjectsCommand = projectsPatterns.some(pattern => pattern.test(command));
        const isContactsCommand = contactsPatterns.some(pattern => pattern.test(command));
        const isIdeasCommand = ideasPatterns.some(pattern => pattern.test(command));
        
        if (isWebrollCommand) {
          console.log('🎯 Smart AI detected webroll command:', command);
          await this.handleURLShowcaseCommand(this.aiPrompt);
          return;
        }

        // Tag Management: add/remove/rename
        if (tagAddHerePattern.test(command)) {
          const [, , tag] = command.match(tagAddHerePattern);
          await this.handleTagAddRemoveHere('add', tag);
          return;
        }
        if (tagRemoveHerePattern.test(command)) {
          const [, , tag] = command.match(tagRemoveHerePattern);
          await this.handleTagAddRemoveHere('remove', tag);
          return;
        }
        if (tagAddPattern.test(command)) {
          const m = command.match(tagAddPattern);
          const tag = m[2];
          const cat = m[3];
          const title = (m[4] || m[5] || '').trim();
          await this.handleTagAddRemoveNamed('add', tag, cat, title);
          return;
        }
        if (tagRemovePattern.test(command)) {
          const m = command.match(tagRemovePattern);
          const tag = m[2];
          const cat = m[3];
          const title = (m[4] || m[5] || '').trim();
          await this.handleTagAddRemoveNamed('remove', tag, cat, title);
          return;
        }
        if (tagRenamePattern.test(command)) {
          const [, oldTag, newTag, scopeWord] = command.match(tagRenamePattern);
          await this.handleTagRename(oldTag, newTag, scopeWord);
          return;
        }

        // Handle tag-only cross-category view
        if (tagOnlyMatch) {
          const tag = tagOnlyMatch[1].toLowerCase();
          await this.handleTagCrossCategoryCommand(tag);
          return;
        }
        // Handle "my #tag <category>" view
        if (tagWithCategoryMatch) {
          const tag = tagWithCategoryMatch[1].toLowerCase();
          const catWord = tagWithCategoryMatch[2].toLowerCase();
          await this.handleTagCategoryCommand(tag, catWord);
          return;
        }
        
        if (isNotesCommand) {
          console.log('📝 Smart AI detected notes command:', command);
          await this.handleCategoryShowcaseCommand('note', 'notes', command);
          return;
        }
        
        if (isProjectsCommand) {
          console.log('📁 Smart AI detected projects command:', command);
          await this.handleCategoryShowcaseCommand('project', 'projects', command);
          return;
        }
        
        if (isContactsCommand) {
          console.log('👤 Smart AI detected contacts command:', command);
          await this.handleCategoryShowcaseCommand('contact', 'contacts', command);
          return;
        }
        
        if (isIdeasCommand) {
          console.log('💡 Smart AI detected ideas command:', command);
          await this.handleCategoryShowcaseCommand('idea', 'ideas', command);
          return;
        }
        
        const response = await geminiService.handleKBCommand(this.aiPrompt);
        
        // Handle special response types
        if (typeof response === 'object' && response.type === 'bulk-import') {
          console.log('📦 Bulk import response detected');
          console.log('📦 Setting showBulkImportForm = true');
          console.log('📦 Setting lastAction = bulk-import');
          this.aiResponse = response.message;
          this.showBulkImportForm = true;
          this.lastAction = 'bulk-import';
          // Bring bulk import form to top and remount
          this.showAICommand = false;
          this.bumpOrder('bulk-import');
          this.bulkImportKey++;
          
          console.log('📦 State after changes:', {
            showBulkImportForm: this.showBulkImportForm,
            lastAction: this.lastAction
          });
          
          // Clear prompt after successful execution
          setTimeout(() => {
            this.aiPrompt = '';
          }, 2000);
          return;
        }
        
        // Render AI Answer as a top-of-feed card for consistency
        this.aiAnswerText = typeof response === 'string' ? response : JSON.stringify(response);
        this.showAIAnswer = true;
        this.bumpOrder('ai-answer');
        this.aiAnswerKey++;
        this.showAICommand = true;
        this.aiCardDisabled = true;
        
      } catch (error) {
        console.error('❌ AI command error:', error);
        this.aiResponse = `❌ Error: ${error.message}`;
      } finally {
        this.aiLoading = false;
      }
    },

    // Resolve plural category key from user word (incl webroll)
    _resolvePluralFromWord(word) {
      const map = { note: 'notes', project: 'projects', contact: 'contacts', idea: 'ideas', url: 'webroll', link: 'webroll' };
      const key = (word || '').toLowerCase();
      return map[key] || key;
    },

    // Tag add/remove for the currently open item context (if any)
    async handleTagAddRemoveHere(action, tag) {
      const item = this.reactiveSelectedItem;
      if (!item) { this.aiResponse = '❌ No item is open to modify.'; return; }
      const plural = this._resolvePluralFromWord(item.category);
      const ok = action === 'add'
        ? await this.knowledgeStore.addTagToItem(plural, item.id, tag)
        : await this.knowledgeStore.removeTagFromItem(plural, item.id, tag);
      this.aiResponse = ok ? `✅ ${action === 'add' ? 'Added' : 'Removed'} #${tag} on this ${item.category}.` : `❌ Could not ${action} #${tag}.`;
    },

    // Tag add/remove targeting by category and title
    async handleTagAddRemoveNamed(action, tag, catWord, title) {
      const plural = this._resolvePluralFromWord(catWord);
      const normalizedQuery = (title || '').toLowerCase().trim();
      // Find candidates by title
      const pool = plural === 'webroll'
        ? (this.knowledgeStore.dynamicCategories?.webroll || [])
        : (this.knowledgeStore[plural] || []);
      const candidates = pool.filter(it => ((it.title || it.name || '').toLowerCase().trim()) === normalizedQuery);
      if (candidates.length === 0) { this.aiResponse = `❌ No ${plural} found titled "${title}".`; return; }
      if (candidates.length > 1) {
        // Show chooser
        this.tagChooserAction = { action, tag, category: plural, title };
        this.tagChooserCandidates = candidates.map(it => ({ id: it.id, title: it.title, name: it.name, category: it.category || (plural.slice(0, -1)) }));
        this.showTagChooser = true;
        this.bumpOrder('tag-chooser');
        this.tagChooserKey++;
        this.aiResponse = '';
        return;
      }
      const target = candidates[0];
      const ok = action === 'add'
        ? await this.knowledgeStore.addTagToItem(plural, target.id, tag)
        : await this.knowledgeStore.removeTagFromItem(plural, target.id, tag);
      this.aiResponse = ok ? `✅ ${action === 'add' ? 'Added' : 'Removed'} #${tag} on ${plural.slice(0, -1)} "${title}".` : `❌ Could not ${action} #${tag}.`;
    },

    // Tag rename with optional scope
    async handleTagRename(oldTag, newTag, scopeWord) {
      const scopeMap = { notes: 'notes', projects: 'projects', contacts: 'contacts', ideas: 'ideas', webroll: 'webroll' };
      const scope = scopeWord ? scopeMap[(scopeWord || '').toLowerCase()] || 'global' : 'global';
      // Dry run preview (fast, from tagIndex)
      const preview = await this.knowledgeStore.renameTag(oldTag, newTag, scope, { dryRun: true });
      const total = Object.values(preview.counts || {}).reduce((a, b) => a + b, 0);
      if (total === 0) { this.aiResponse = `ℹ️ No items found with #${oldTag}${scope === 'global' ? '' : ' in ' + scope}.`; return; }
      // For now, apply immediately (no UI confirm). We can add a preview card later.
      const result = await this.knowledgeStore.renameTag(oldTag, newTag, scope, { dryRun: false });
      const summary = Object.entries(result.counts || {}).filter(([, c]) => c > 0).map(([k, c]) => `${k}:${c}`).join(', ');
      this.aiResponse = `✅ Renamed #${oldTag} → #${newTag} (${summary || total + ' items'})`;
    },
    // Chooser interactions
    async selectTagChooserCandidate(cand) {
      if (!this.tagChooserAction) { this.cancelTagChooser(); return; }
      const { action, tag, category } = this.tagChooserAction;
      // Snapshot previous tags for undo
      const pool = category === 'webroll' ? (this.knowledgeStore.dynamicCategories?.webroll || []) : (this.knowledgeStore[category] || []);
      const item = pool.find(it => it.id === cand.id);
      const prev = Array.isArray(item?.tags) ? [...item.tags] : [];
      const ok = action === 'add'
        ? await this.knowledgeStore.addTagToItem(category, cand.id, tag)
        : await this.knowledgeStore.removeTagFromItem(category, cand.id, tag);
      // Replace chooser with success card and pause AI chat
      if (ok) {
        this.showTagChooser = false;
        this.tagChooserCandidates = [];
        this.tagOpSuccessData = { action, tag, item: { id: cand.id, title: cand.title, name: cand.name, category: cand.category }, previousTags: prev };
        this.showTagOpSuccess = true;
        this.bumpOrder('tag-op-success');
        this.tagOpSuccessKey++;
        this.aiCardDisabled = true;
        this.aiResponse = '';
      } else {
        this.aiResponse = `❌ Could not ${action} #${tag}.`;
        this.cancelTagChooser();
      }
    },
    cancelTagChooser() {
      this.showTagChooser = false;
      this.tagChooserCandidates = [];
      this.tagChooserAction = null;
    },
    // Success card actions
    async viewTagOpItem() {
      const d = this.tagOpSuccessData; if (!d) return;
      const pool = d.item.category === 'webroll' ? (this.knowledgeStore.dynamicCategories?.webroll || []) : this.knowledgeStore.allItems;
      let item = pool.find(it => it.id === d.item.id) || { id: d.item.id, category: d.item.category, title: d.item.title, name: d.item.name };
      // Ensure tags field exists for TagEditor
      if (!Array.isArray(item.tags)) item = { ...item, tags: [] };
      this.openItemDetails({ ...item, category: d.item.category });
    },
    askAIFromTagOp() {
      const d = this.tagOpSuccessData; if (!d) return;
      this.showAICommand = true;
      this.aiPrompt = '';
      this.aiResponse = '';
      this.bumpOrder('ai');
      this.aiKey++;
      this.aiCardDisabled = false;
    },
    async undoTagOp() {
      const d = this.tagOpSuccessData; if (!d) return;
      const category = this._resolvePluralFromWord(d.item.category);
      // Restore previous tags snapshot safely (merge-only)
      await this.knowledgeStore.updateItemTags(category, d.item.id, d.previousTags || []);
      this.showTagOpSuccess = false; this.tagOpSuccessData = null;
    },
    dismissTagOpSuccess() {
      this.showTagOpSuccess = false;
      this.tagOpSuccessData = null;
    },
    enableAIChat() { this.aiCardDisabled = false; this.bumpOrder('ai'); this.aiKey++; this.aiPrompt = ''; this.aiResponse = ''; },
    // AI Answer actions
    continueChatFromAnswer() {
      this.showAICommand = true;
      this.aiCardDisabled = false;
      this.aiPrompt = '';
      this.aiResponse = '';
      this.bumpOrder('ai');
      this.aiKey++;
    },
    createNoteFromAnswer() {
      // Quick-fill from lastAIPrompt and aiAnswerText
      const titleBase = (this.lastAIPrompt || '').trim();
      const title = titleBase.length > 60 ? titleBase.slice(0, 57) + '…' : (titleBase || 'New Note');
      const body = (this.aiAnswerText || '').trim();
      const tags = Array.from(new Set((title + ' ' + body)
        .toLowerCase()
        .match(/#[a-z0-9-]+/g) || []))
        .map(t => t.replace('#', ''))
        .slice(0, 6);

      // Open note context on top with prefilled fields
      this.showNoteContext = true;
      this.bumpOrder('note');
      this.createKeys.note++;
      this.noteTitle = title;
      this.noteContent = body;
      this.noteTags = tags;
      // Build lightweight suggestions: from tagIndex + relaxed keyword mining
      const corpus = (this.lastAIPrompt + ' ' + body + ' ' + title).toLowerCase();
      const tagIndexKeys = Object.keys(this.knowledgeStore.tagIndex || {});
      const fromExisting = tagIndexKeys.filter(k => corpus.includes(k)).slice(0, 12);
      // Keyword mining (3+ chars), exclude stopwords and numbers, rank by frequency
      const stop = new Set(['the','and','for','with','that','this','you','your','from','into','over','about','into','are','was','were','have','has','had','but','not','all','any','can','just','more','most','some','such','than','then','them','they','their','there','here','how','why','what','when','who','which','while','may','will','shall','should','could','would','also','use','using','used','to','of','in','on','at','by','an','a','as','it','is','be','or']);
      const words = (corpus.match(/\b[a-z0-9-]{3,}\b/g) || []).filter(w => !stop.has(w) && !/^\d+$/.test(w));
      const freq = new Map();
      for (const w of words) freq.set(w, (freq.get(w) || 0) + 1);
      const sortedKeywords = Array.from(freq.entries()).sort((a,b) => b[1]-a[1]).map(([w]) => w);
      const fromKeywords = sortedKeywords.filter(w => !fromExisting.includes(w)).slice(0, 10);
      let combined = Array.from(new Set([...tags, ...fromExisting, ...fromKeywords])).slice(0, 12);
      if (combined.length === 0) combined = ['ai','answer'];
      this.suggestedNoteTags = combined;
      this.showSuggestedNoteTags = true; // always show when coming from AI answer
    },
    computeSuggestedNoteTags() {
      // If already set from AI flow, keep; otherwise derive from current title/body
      if (this.suggestedNoteTags && this.suggestedNoteTags.length) { this.showSuggestedNoteTags = true; return; }
      const title = (this.noteTitle || '').toLowerCase();
      const body = (this.noteContent || '').toLowerCase();
      const corpus = (title + ' ' + body + ' ' + (this.lastAIPrompt || '')).trim();
      if (!corpus) { this.suggestedNoteTags = ['ai','answer']; this.showSuggestedNoteTags = true; return; }
      const keys = Object.keys(this.knowledgeStore.tagIndex || {});
      const fromExisting = keys.filter(k => corpus.includes(k)).slice(0, 8);
      const stop = new Set(['the','and','for','with','that','this','you','your','from','into','over','about','into','are','was','were','have','has','had','but','not','all','any','can','just','more','most','some','such','than','then','them','they','their','there','here','how','why','what','when','who','which','while','may','will','shall','should','could','would','also','use','using','used','to','of','in','on','at','by','an','a','as','it','is','be','or']);
      const words = (corpus.match(/\b[a-z0-9-]{3,}\b/g) || []).filter(w => !stop.has(w) && !/^\d+$/.test(w));
      const uniq = Array.from(new Set(words)).slice(0, 10);
      let combined = Array.from(new Set([...fromExisting, ...uniq])).slice(0, 12);
      if (combined.length === 0) combined = ['ai','answer'];
      this.suggestedNoteTags = combined;
      this.showSuggestedNoteTags = true;
    },
    dismissAIAnswer() {
      this.showAIAnswer = false;
      this.aiAnswerText = '';
    },
    addSuggestedNoteTag(t) {
      const tag = String(t).toLowerCase();
      if (!this.noteTags.includes(tag)) this.noteTags.push(tag);
    },
    
    async handleURLShowcaseCommand(command) {
      try {
        const commandLower = command.toLowerCase();
        
        // 🌐 Check if it's a general webroll request
        if (commandLower.includes('webroll') || commandLower.includes('web roll')) {
          console.log('🌐 Detected general webroll request');
          
          // Show ALL webroll items (category or tag)
          const allItems = this.knowledgeStore.allItems;
          const urlItems = allItems.filter(item => 
            item.category === 'webroll' || 
            (item.tags && item.tags.some(tag => 
              tag.toLowerCase().includes('webroll') || 
              tag.toLowerCase() === 'webroll'
            )) ||
            (item.type === 'url' && item.category === 'webroll')
          );
          
          console.log(`🔍 Found ${urlItems.length} total webroll URLs`);
          
          if (urlItems.length === 0) {
            this.aiResponse = '❌ No URLs found in your webroll yet.';
            this.showAICommand = true;
            this.lastAction = 'ai';
            return;
          }
          
          // Hide AI command and show URL showcase
          this.showAICommand = false;
          this.showURLsByCategory(urlItems, 'webroll', 'webroll');
          this.aiPrompt = '';
          return;
        }
        
        // 🏷️ Extract specific tag from command (handle "my #tech URLs", "my tech URLs", "my daily webroll")
        let tagMatch = command.match(/my\s+#?([\w-]+)(?:\s+(urls?|links?|webroll)|\s*$)/i);
        
        // If no match, try alternative patterns
        if (!tagMatch) {
          tagMatch = command.match(/#([\w-]+)/); // Just find any #tag
        }
        
        const tag = tagMatch ? tagMatch[1] : 'daily';
        console.log(`🔍 DEBUG: Extracted tag "${tag}" from command "${command}"`);
        
        // Get all items and filter for URLs with the specific tag
        const allItems = this.knowledgeStore.allItems;
        const urlItems = allItems.filter(item => 
          item.type === 'url' && 
          item.tags && 
          item.tags.includes(tag)
        );
        
        console.log(`🔍 Found ${urlItems.length} URLs with tag #${tag}`);
        
        if (urlItems.length === 0) {
          this.aiResponse = `❌ No URLs found with tag #${tag}`;
          return;
        }
        
        // Hide AI command and show URL showcase
        this.showAICommand = false;
        this.showURLsByCategory(urlItems, tag, 'webroll');
        
        // Clear prompt
        this.aiPrompt = '';
        
      } catch (error) {
        console.error('❌ Error handling URL showcase:', error);
        this.aiResponse = `❌ Error retrieving URLs: ${error.message}`;
      }
    },
    
    // 📝📁👤💡 Category Showcase Command Handler (like Webroll)
    async handleCategoryShowcaseCommand(category, pluralName, command) {
      try {
        const commandLower = command.toLowerCase();
        
        console.log(`🎯 Detected general ${pluralName} request`);
        
        // Show ALL items of this category
        const allItems = this.knowledgeStore.allItems;
        const categoryItems = allItems.filter(item => item.category === category);
        
        console.log(`🔍 Found ${categoryItems.length} total ${pluralName}`);
        
        if (categoryItems.length === 0) {
          const emoji = this.getItemIcon(category);
          this.aiResponse = `❌ No ${pluralName} found yet. Create your first ${category} to get started!`;
          this.showAICommand = true;
          this.lastAction = 'ai';
          return;
        }
        
        // Hide AI command and show item showcase
        this.showAICommand = false;
        this.showItemsByCategory(categoryItems, category, pluralName);
        this.aiPrompt = '';
        
      } catch (error) {
        console.error(`❌ Error handling ${pluralName} showcase:`, error);
        this.aiResponse = `❌ Error retrieving ${pluralName}: ${error.message}`;
        this.showAICommand = true;
        this.lastAction = 'ai';
      }
    },

    // 🏷️ Cross-category tag view
    async handleTagCrossCategoryCommand(tag) {
      const ks = this.knowledgeStore;
      const index = ks.tagIndex || {};
      const bucket = index[tag];
      if (!bucket) {
        this.aiResponse = `❌ No items found with tag #${tag}`;
        return;
      }
      // When a tag is queried, reflect it in the filter bar
      
      // Build a summary card and open filtered showcases per category with results
      const allItems = ks.allItems;
      const byId = new Map(allItems.map(i => [i.id, i]));
      const build = (ids, category) => ids
        .map(id => byId.get(id))
        .filter(it => it && (category === 'webroll' ? (it.type === 'url' || it.category === 'webroll') : it.category === category));
      const notes = build(bucket.notes, 'note');
      const projects = build(bucket.projects, 'project');
      const contacts = build(bucket.contacts, 'contact');
      const ideas = build(bucket.ideas, 'idea');
      const urls = build(bucket.webroll, 'webroll');

      // Prefer showing the most populated category first; also show URLs if any
      const order = [
        { items: urls, tag: tag, kind: 'webroll', show: (arr) => this.showURLsByCategory(arr, tag, 'webroll') },
        { items: notes, tag: tag, kind: 'notes', show: (arr) => this.showItemsByCategory(arr, 'note', 'notes') },
        { items: projects, tag: tag, kind: 'projects', show: (arr) => this.showItemsByCategory(arr, 'project', 'projects') },
        { items: contacts, tag: tag, kind: 'contacts', show: (arr) => this.showItemsByCategory(arr, 'contact', 'contacts') },
        { items: ideas, tag: tag, kind: 'ideas', show: (arr) => this.showItemsByCategory(arr, 'idea', 'ideas') }
      ];
      // Show each non-empty set; latest shown will be on top via bump inside methods
      let shown = 0;
      for (const entry of order) {
        if (entry.items.length) {
          entry.show(entry.items);
          shown++;
        }
      }
      if (!shown) this.aiResponse = `❌ No items found with tag #${tag}`;
      else this.aiResponse = `🔎 Showing items tagged #${tag}`;
      this.aiPrompt = '';
    },

    // 🏷️ Single-category tag view
    async handleTagCategoryCommand(tag, catWord) {
      const map = { note: 'note', notes: 'note', project: 'project', projects: 'project', contact: 'contact', contacts: 'contact', idea: 'idea', ideas: 'idea', url: 'webroll', urls: 'webroll', links: 'webroll', webroll: 'webroll' };
      const category = map[catWord] || 'note';
      const ks = this.knowledgeStore;
      const index = ks.tagIndex || {};
      const bucket = index[tag];
      if (!bucket) { this.aiResponse = `❌ No ${catWord} found with tag #${tag}`; return; }
      // When a tag is queried, reflect it in the filter bar
      
      const allItems = ks.allItems;
      const byId = new Map(allItems.map(i => [i.id, i]));
      const ids = category === 'note' ? bucket.notes
        : category === 'project' ? bucket.projects
        : category === 'contact' ? bucket.contacts
        : category === 'idea' ? bucket.ideas
        : bucket.webroll;
      const items = ids.map(id => byId.get(id)).filter(Boolean).filter(it => category === 'webroll' ? (it.type === 'url' || it.category === 'webroll') : it.category === category);
      if (!items.length) { this.aiResponse = `❌ No ${catWord} found with tag #${tag}`; return; }
      if (category === 'webroll') this.showURLsByCategory(items, tag, 'webroll');
      else this.showItemsByCategory(items, category, category + 's');
      this.aiPrompt = '';
    },
    
    // Item Showcase methods
    async showItemsByCategory(items, category, pluralName) {
      this.lastAction = 'item-showcase';
      this.showItemShowcase = true; // legacy flag unused in new stacked cards but kept for compat

      // Clear any previous internal selection
      this.internalSelectedItem = null;

      const ids = items.map(it => it.id);
      // If a showcase for this category already exists, update and bump it
      const existing = this.itemShowcases.find(c => c.category === category);
      if (existing) {
        existing.ids = ids;
        existing.pluralName = pluralName;
        existing.key = `${category}-${Date.now()}-${existing.id}`; // force remount
        // Move to top of the stack
        this.itemShowcases = [existing, ...this.itemShowcases.filter(c => c !== existing)];
        this.bumpOrder(`item-showcase-${existing.id}`);
        return;
      }

      // Otherwise, create a new stacked showcase card
      const cardId = ++this.itemShowcaseIdCounter;
      const card = {
        id: cardId,
        key: `${category}-${Date.now()}-${cardId}`,
        category,
        pluralName,
        ids
      };
      this.itemShowcases.unshift(card);
      this.bumpOrder(`item-showcase-${cardId}`);
    },
    
    // Toggle item expansion (for long content)
    toggleItemExpansion(itemId) {
      if (this.expandedItemIds.has(itemId)) {
        this.expandedItemIds.delete(itemId);
      } else {
        this.expandedItemIds.add(itemId);
      }
      // Force reactivity update
      this.expandedItemIds = new Set(this.expandedItemIds);
    },
    
    // Check if item is expanded
    isItemExpanded(itemId) {
      return this.expandedItemIds.has(itemId);
    },
    

    
    // Delete item from showcase
    async deleteItemFromShowcase(item) {
      console.log(`🗑️ Deleting ${item.category} from showcase:`, item);
      
      if (!confirm(`Are you sure you want to delete this ${item.category}?`)) {
        return;
      }
      
      try {
        const knowledgeStore = useKnowledgeStore();
        
        // Delete from Firebase - use correct plural category name
        const categoryMap = {
          'note': 'notes',
          'project': 'projects',
          'contact': 'contacts', 
          'idea': 'ideas'
        };
        const correctCategory = categoryMap[item.category] || item.category;
        
        await knowledgeStore.deleteItem(correctCategory, item.id);
        
        knowledgeStore.addNotification('item-deleted', '✅ Item Deleted', `${this.getItemTypeDisplay(item.category)} deleted successfully!`);
        
        console.log(`✅ ${item.category} deleted successfully`);
      } catch (error) {
        console.error(`❌ Error deleting ${item.category}:`, error);
        alert(`Error deleting ${item.category}. Please try again.`);
      }
    },
    
    // Open item details (similar to URL click) - persist context card and bump order
    openItemDetails(item) {
      console.log(`🔍 Opening ${item.category} details:`, item);
      
      // Use internal state instead of mutating prop
      this.internalSelectedItem = item;
      // Show item context card persistently and bring it to top
      this.showItemContext = true;
      this.bumpOrder('item-context');
      this.itemContextKey++;
    },
    
    // URL Showcase methods
    async showURLsByCategory(urls, tag, category) {
      this.lastAction = 'url-showcase';
      this.showURLShowcase = true;
      
      // Set up reactive filter instead of static array
      this.showcaseURLsFilter = (item) => {
        return urls.some(url => url.id === item.id);
      };
      
      this.showcaseTag = tag;
      this.showcaseCategory = category;
      // Bring to top and remount for freshness
      this.bumpOrder('url-showcase');
      this.showcaseKeys.url++;
      
      // Load logos for URLs in the background
      this.loadLogosForURLs(urls);
    },

    // 🖼️ Load logos for URL items
    async loadLogosForURLs(urls) {
      for (const urlItem of urls) {
        if (urlItem.url && !urlItem.logo) {
          try {
            const logo = await logoService.getLogo(urlItem.url, urlItem.title);
            // Add logo to the URL item
            urlItem.logo = logo;
            this.$forceUpdate(); // Trigger reactivity
          } catch (error) {
            console.warn('🖼️ Failed to load logo for:', urlItem.url, error);
            urlItem.logo = { type: 'emoji', src: '🔗' };
          }
        }
      }
    },

    // Handle logo loading errors
    handleURLLogoError(urlItem) {
      console.warn('🖼️ Logo load error for:', urlItem.url);
      urlItem.logo = { type: 'emoji', src: '🔗' };
      this.$forceUpdate();
    },
    
    dismissURLShowcase() {
      this.showURLShowcase = false;
      this.showcaseURLsFilter = null;
      this.showcaseTag = '';
      this.showcaseCategory = '';
    },
    
    openURL(url, title) {
      // Enhanced browser detection and optimal opening strategy
      this.openWithEnhancedBrowser(url, title);
    },
    
    // Enhanced browser opening with PWA detection and smart fallbacks
    openWithEnhancedBrowser(url, title = '') {
      // Detect if running as installed PWA
      const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                   window.navigator.standalone ||
                   document.referrer.includes('android-app://');
      
      // Detect mobile device
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // For PWA mode, try enhanced browser opening first
      if (isPWA) {
        this.openInEnhancedPWAMode(url, title);
      } else if (isMobile) {
        this.openInMobileOptimized(url, title);
      } else {
        // Fallback to current webview for desktop browsers
        this.openInAppWebview(url, title);
      }
    },
    
    // Enhanced PWA mode opening (like Facebook/Yahoo apps)
    openInEnhancedPWAMode(url, title = '') {
      try {
        // Enhanced window.open with PWA-optimized parameters
        const windowFeatures = [
          'popup=yes',
          'width=' + (window.screen.width * 0.9),
          'height=' + (window.screen.height * 0.9),
          'left=' + (window.screen.width * 0.05),
          'top=' + (window.screen.height * 0.05),
          'scrollbars=yes',
          'resizable=yes',
          'status=no',
          'toolbar=no',
          'menubar=no',
          'location=no'
        ].join(',');
        
        const newWindow = window.open(url, '_blank', windowFeatures);
        
        if (newWindow) {
          // Success! Enhanced browser opening worked
          console.log('🚀 Enhanced PWA browser opening successful');
          
          // Optional: Track window for enhanced UX
          this.trackEnhancedWindow(newWindow, url, title);
        } else {
          // Fallback to in-app webview
          console.log('📱 PWA browser blocked, falling back to webview');
          this.openInAppWebview(url, title);
        }
      } catch (error) {
        console.log('⚠️ Enhanced PWA opening failed, using webview:', error);
        this.openInAppWebview(url, title);
      }
    },
    
    // Mobile-optimized opening
    openInMobileOptimized(url, title = '') {
      try {
        // Try opening with mobile-optimized parameters
        const mobileWindow = window.open(url, '_blank', 'noopener,noreferrer');
        
        if (mobileWindow) {
          console.log('📱 Mobile-optimized opening successful');
        } else {
          // Fallback to webview
          this.openInAppWebview(url, title);
        }
      } catch (error) {
        this.openInAppWebview(url, title);
      }
    },
    
    // Track enhanced windows for better UX (optional feature)
    trackEnhancedWindow(windowRef, url, title) {
      // This is where we could add enhanced tracking/integration
      // For now, just log the success
      console.log(`🎯 Enhanced browser opened: ${title || url}`);
      
      // Optional: Add to a list of open windows for advanced management
      // We could implement window communication here in the future
    },
    
    // Webview management methods
    async openInAppWebview(url, title = '') {
      console.log(`🎯 Opening URL: ${url}`);
      
      // 🔧 Check if site is in user's broken sites list FIRST
      const hostname = new URL(url).hostname;
      if (this.brokenSitesList.includes(hostname)) {
        console.log(`🔧 ${hostname} is in broken sites list - showing fallback immediately`);
        
        // 📍 IMPORTANT: Still save scroll position even for broken sites!
        const postsContainer = document.querySelector('.posts-container');
        const scrollPosition = postsContainer ? postsContainer.scrollTop : 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollPosition}px`;
        document.body.style.width = '100%';
        
        // Show webview with fallback UI
        this.webviewURL = url;
        this.webviewTitle = title || this.extractDomain(url);
        this.webviewLoading = false;
        this.webviewError = true;
        this.showWebview = true;
        this.showURLShowcase = false;
        return;
      }
      
      // 📍 Lock scroll position
      const postsContainer = document.querySelector('.posts-container');
      const scrollPosition = postsContainer ? postsContainer.scrollTop : 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
      
      this.webviewURL = url;
      this.webviewTitle = title || this.extractDomain(url);
      this.webviewLoading = true;
      this.webviewError = false;
      this.showWebview = true;
      this.showURLShowcase = false;
      
      // ⚡ Domain-based embed cache (24h TTL)
      try {
        const host = new URL(url).host;
        const cacheKey = `embedCheck:${host}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed.canEmbed === 'boolean' && parsed.ts && (Date.now() - parsed.ts < 24 * 60 * 60 * 1000)) {
            console.log(`🗂️ Cache hit for ${host}:`, parsed.canEmbed ? 'can embed' : 'blocked');
            if (!parsed.canEmbed) {
              this.webviewLoading = false;
              this.webviewError = true;
              return;
            } else {
              this.addPreconnect(url);
            }
          }
        }
      } catch (e) {
        console.log('⚠️ Embed cache read failed:', e);
      }
      
      try {
        // Check with backend first
        console.log('🔍 Checking iframe compatibility...');
        const checkResponse = await fetch('http://localhost:4001/api/check-iframe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url })
        });
        const { canEmbed, reason } = await checkResponse.json();
        
        console.log(`📋 Check result: ${canEmbed ? '✅' : '🚫'} - ${reason}`);
        
        // Update cache
        try {
          const host = new URL(url).host;
          localStorage.setItem(`embedCheck:${host}`, JSON.stringify({ canEmbed, ts: Date.now() }));
        } catch (e) {
          console.log('⚠️ Embed cache write failed:', e);
        }
        
        if (!canEmbed) {
          // Backend says it's blocked - show options immediately
          console.log('🚫 URL blocked by backend - showing options');
          this.webviewLoading = false;
          this.webviewError = true;
          return;
        }
        
        // Backend says it's safe - iframe will render and onWebviewLoad will handle success
        console.log('✅ Backend approved - iframe will load');
        this.addPreconnect(url);
        
        // 🎯 BACKEND SAYS CAN EMBED - SET UP MULTI-LAYER DETECTION!
        console.log('✅ Backend says can embed - setting up hybrid detection system');
        
        // 🚨 METHOD 1: CONSOLE ERROR LISTENER (for "Refused to frame" messages)
        this.setupConsoleErrorListener(url);
        
        // 🚨 METHOD 2: CSP VIOLATION LISTENER (backup)
        this.setupCSPViolationListener(url);
        
        console.log('✅ Detection systems ready - rendering iframe now');
        this.webviewLoading = false; // This allows iframe to render!
        
      } catch (error) {
        console.log('❌ Backend check failed - showing options');
        this.webviewLoading = false;
        this.webviewError = true;
      }
    },
    
    // Add preconnect/dns-prefetch hints to speed initial connections
    addPreconnect(targetURL) {
      try {
        const { protocol, host } = new URL(targetURL);
        const origin = `${protocol}//${host}`;
        const attr = `data-preconnect-host`;
        if (!document.head.querySelector(`link[rel="preconnect"][${attr}="${host}"]`)) {
          const pre = document.createElement('link');
          pre.rel = 'preconnect';
          pre.href = origin;
          pre.setAttribute(attr, host);
          pre.crossOrigin = '';
          document.head.appendChild(pre);
        }
        if (!document.head.querySelector(`link[rel="dns-prefetch"][${attr}="${host}"]`)) {
          const dns = document.createElement('link');
          dns.rel = 'dns-prefetch';
          dns.href = origin;
          dns.setAttribute(attr, host);
          document.head.appendChild(dns);
        }
        console.log(`🔌 Preconnect hints added for ${host}`);
      } catch (e) {
        console.log('⚠️ Preconnect failed:', e);
      }
    },
    
    // 🎯 NEW: Generate proxy URL for enhanced iframe compatibility
    getProxyURL(originalURL) {
      try {
        // 🚀 SIMPLIFIED: For dev mode, just enhance the original URL with better parameters
        if (this.shouldUseEnhancedMode(originalURL)) {
          return this.createEnhancedURL(originalURL);
        }
        
        // Check if Service Worker is available and active (for production)
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          // Add proxy parameter to trigger Service Worker proxying
          const url = new URL(originalURL);
          url.searchParams.set('_webview_proxy', 'true');
          console.log('🌐 Using Service Worker proxy for:', originalURL);
          return url.href;
        } else {
          // Service Worker not available, use original URL
          console.log('📱 Service Worker not active, using original URL');
          return originalURL;
        }
      } catch (error) {
        console.log('⚠️ Proxy URL generation failed, using original:', error);
        return originalURL;
      }
    },
    
    // 🔥 NEW: Determine if we should use enhanced mode (for dev mode)
    shouldUseEnhancedMode(url) {
      // Use enhanced mode for development
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      return isDev;
    },
    
    // 🎯 NEW: Create enhanced URL with better iframe parameters
    createEnhancedURL(originalURL) {
      try {
        // For now, just return the original URL but log that we're in enhanced mode
        console.log('🔧 Enhanced mode for:', originalURL);
        
        // Add a small delay and better error handling in the iframe
        return originalURL;
        
      } catch (error) {
        console.log('⚠️ Enhanced URL creation failed:', error);
        return originalURL;
      }
    },
    
    closeWebview() {
      // 🛑 Clean up error listeners when closing
      this.stopErrorListeners();
      
      // 📍 Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      this.showWebview = false;
      this.webviewURL = '';
      this.webviewTitle = '';
      this.webviewLoading = false;
      this.webviewError = false;
      
      // Return to URL showcase if it was previously open
      if (this.showcaseURLsFilter) {
        this.showURLShowcase = true;
      }
      
      // 📍 Restore scroll position in posts container
      this.$nextTick(() => {
        if (scrollY) {
          const postsContainer = document.querySelector('.posts-container');
          if (postsContainer) {
            postsContainer.scrollTop = parseInt(scrollY || '0') * -1;
          }
        }
      });
    },
    
    onWebviewLoad() {
      console.log('✅ Iframe loaded - using Performance API detection (ChatGPT method)');
      this.webviewLoading = false;
      this.webviewError = false;
      
      // 🎯 PERFORMANCE API + SMART TIMEOUT (ChatGPT's hybrid approach)
      let checkCount = 0;
      const maxChecks = 10; // 2 seconds max (200ms intervals)
      const originalSrc = this.webviewURL;
      
      const performanceDetection = () => {
        checkCount++;
        const iframe = this.$refs.webviewIframe;
        
        if (!iframe) {
          console.log('❌ No iframe found - showing fallback');
          this.webviewLoading = false;
          this.webviewError = true;
          this.stopErrorListeners();
          return;
        }
        
        // 🔥 PERFORMANCE API SNIFFING (ChatGPT's key insight)
        const perfEntries = performance.getEntriesByType("resource");
        const networkEntry = perfEntries.find(e => e.name.includes(new URL(originalSrc).hostname));
        
        console.log(`🔍 Check ${checkCount}: Performance entry found:`, !!networkEntry);
        
        if (networkEntry) {
          // Network request completed - now check if iframe actually rendered content
          
          try {
            // Try to detect if iframe has interactive content
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            const hasInteractiveContent = iframe.contentWindow && iframe.offsetHeight > 0;
            
            console.log(`🔍 Interactive content detected:`, hasInteractiveContent);
            
            if (hasInteractiveContent) {
              console.log('✅ Performance entry + interactive content = WORKING!');
              this.stopErrorListeners();
              return;
            } else {
              // Network completed but no interactive content = likely CSP blocked
              if (checkCount >= 4) { // Wait 800ms (like ChatGPT suggested) before assuming blocked
                console.log('🚫 Performance entry exists but no interactive content = CSP BLOCKED - showing fallback!');
                this.webviewLoading = false;
                this.webviewError = true;
                this.stopErrorListeners();
                return;
              }
            }
          } catch (e) {
            // Cross-origin access error is normal - check if iframe seems "alive"
            const seemsAlive = iframe.offsetHeight > 0 && iframe.offsetWidth > 0;
            console.log(`🔍 Cross-origin but seems alive:`, seemsAlive);
            
            if (seemsAlive && checkCount >= 3) { // Give cross-origin sites benefit of doubt
              console.log('✅ Cross-origin but seems alive = WORKING!');
              this.stopErrorListeners();
              return;
            }
          }
        } else {
          console.log('⏳ No performance entry yet - still loading...');
        }
        
        // Keep checking if we haven't reached max attempts
        if (checkCount < maxChecks) {
          setTimeout(performanceDetection, 200);
        } else {
          console.log('✅ Timeout reached - assuming site works (slow loading)');
          this.stopErrorListeners();
        }
      };
      
      // Start detection after brief delay to let performance entries populate
      setTimeout(performanceDetection, 300);
    },

    setupConsoleErrorListener(url) {
      console.log('🎯 Setting up console error listener for "Refused to frame"');
      const hostname = new URL(url).hostname;
      
      // Override console.error to catch "Refused to frame" messages
      this.originalConsoleError = console.error;
      console.error = (...args) => {
        const message = args.join(' ');
        
        if (message.includes('Refused to frame') && message.includes(hostname)) {
          console.log('🚫 DETECTED: Console "Refused to frame" for our site - showing fallback!');
          this.webviewLoading = false;
          this.webviewError = true;
          this.stopErrorListeners();
          return;
        }
        
        if (message.includes('Refused to display') && message.includes(hostname)) {
          console.log('🚫 DETECTED: Console "Refused to display" for our site - showing fallback!');
          this.webviewLoading = false;
          this.webviewError = true;
          this.stopErrorListeners();
          return;
        }
        
        // Call original console.error
        if (this.originalConsoleError) {
          this.originalConsoleError.apply(console, args);
        }
      };
    },

    setupCSPViolationListener(url) {
      console.log('🎯 Setting up CSP violation listener as backup');
      const hostname = new URL(url).hostname;
      
      this.cspViolationHandler = (event) => {
        console.log('🚨 CSP Violation detected:', event);
        if (event.blockedURI && event.blockedURI.includes(hostname)) {
          console.log('🚫 DETECTED: CSP violation for our iframe - showing fallback!');
          this.webviewLoading = false;
          this.webviewError = true;
          this.stopErrorListeners();
        }
      };
      window.addEventListener('securitypolicyviolation', this.cspViolationHandler);
    },

    stopErrorListeners() {
      console.log('🛑 STOPPING ALL ERROR LISTENERS - SITE IS WORKING!');
      
      // Restore console.error
      if (this.originalConsoleError) {
        console.error = this.originalConsoleError;
        this.originalConsoleError = null;
      }
      
      // Remove CSP violation listener
      if (this.cspViolationHandler) {
        window.removeEventListener('securitypolicyviolation', this.cspViolationHandler);
        this.cspViolationHandler = null;
      }
      
      // Restore original window.onerror
      if (this.originalWindowError !== undefined) {
        window.onerror = this.originalWindowError;
        this.originalWindowError = undefined;
      }
      
      // Clear any timeouts
      if (this.errorCheckTimeout) {
        clearTimeout(this.errorCheckTimeout);
        this.errorCheckTimeout = null;
      }
    },
    
    onWebviewError() {
      console.log('🚫 Iframe error detected - showing fallback');
      this.webviewLoading = false;
      this.webviewError = true;
    },
    
    openInNewTab() {
      // 🎯 ENHANCED UX: Open in a smaller, controlled window
      const screenWidth = window.screen.availWidth;
      const screenHeight = window.screen.availHeight;
      
      // Make window 80% of screen size, centered
      const windowWidth = Math.floor(screenWidth * 0.8);
      const windowHeight = Math.floor(screenHeight * 0.8);
      const left = Math.floor((screenWidth - windowWidth) / 2);
      const top = Math.floor((screenHeight - windowHeight) / 2);
      
      const windowFeatures = [
        `width=${windowWidth}`,
        `height=${windowHeight}`,
        `left=${left}`,
        `top=${top}`,
        'menubar=no',      // Hide menu bar
        'toolbar=no',      // Hide toolbar  
        'location=yes',    // Keep address bar (for security/trust)
        'status=no',       // Hide status bar
        'resizable=yes',   // Allow resize
        'scrollbars=yes'   // Allow scrolling
      ].join(',');
      
      const newWindow = window.open(this.webviewURL, '_blank', windowFeatures);
      
      // 🎯 Focus the new window immediately
      if (newWindow) {
        newWindow.focus();
        
        // 🆕 Add a minimal navigation overlay
        try {
          newWindow.addEventListener('load', () => {
            const navBar = newWindow.document.createElement('div');
            navBar.style.cssText = `
              position: fixed; top: 0; left: 0; right: 0; z-index: 999999;
              background: rgba(0,0,0,0.8); color: white; padding: 8px 12px;
              display: flex; justify-content: space-between; align-items: center;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 14px; backdrop-filter: blur(10px);
            `;
            
            navBar.innerHTML = `
              <div style="display: flex; gap: 12px; align-items: center;">
                <button onclick="history.back()" style="background: rgba(255,255,255,0.2); border: none; color: white; padding: 4px 8px; border-radius: 4px; cursor: pointer;">← Back</button>
                <span style="opacity: 0.7;">Browsing from PrivateApp</span>
              </div>
              <button onclick="window.close()" style="background: rgba(255,255,255,0.2); border: none; color: white; padding: 4px 8px; border-radius: 4px; cursor: pointer;">✕ Close</button>
            `;
            
            newWindow.document.body.appendChild(navBar);
            
            // Adjust body margin to account for nav bar
            newWindow.document.body.style.marginTop = '40px';
          });
        } catch (e) {
          console.log('⚠️ Could not add navigation overlay (CORS restriction)');
        }
      }
      
      // 🎯 Keep our modal open so user feels they're still "in our app"
      // Don't close the webview - let them manually close when they're done
      console.log(`🌐 Opened ${this.webviewURL} in enhanced popup window`);
    },

    // Load broken sites list from localStorage
    loadBrokenSitesList() {
      try {
        const stored = localStorage.getItem('brokenSitesList');
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error loading broken sites list:', e);
        return [];
      }
    },

    // Save broken sites list to localStorage
    saveBrokenSitesList() {
      try {
        localStorage.setItem('brokenSitesList', JSON.stringify(this.brokenSitesList));
      } catch (e) {
        console.error('Error saving broken sites list:', e);
      }
    },

    // Mark current site as broken and open in browser
    markSiteAsBroken() {
      const hostname = new URL(this.webviewURL).hostname;
      
      // Add to broken sites list if not already there
      if (!this.brokenSitesList.includes(hostname)) {
        console.log(`🔧 Adding ${hostname} to broken sites list`);
        this.brokenSitesList.push(hostname);
        this.saveBrokenSitesList();
        
        // Clear any cached "can embed" status
        try {
          const cacheKey = `embed_${hostname}`;
          localStorage.removeItem(cacheKey);
        } catch (e) {
          // Ignore cache clear errors
        }
      }
      
      // Show fallback immediately
      this.webviewError = true;
      
      // Open in new tab
      this.openInNewTab();
      
      // Show notification
      this.knowledgeStore.addNotification('site-broken', '🔧 Site Marked Broken', `${hostname} marked as incompatible. It will open externally next time.`);
    },

    // Legacy editURL method - REMOVED (replaced by startInlineEdit)

    // Inline editing methods
    startInlineEdit(urlItem) {
      console.log('✏️ Starting inline edit:', urlItem);
      this.inlineEditingId = urlItem.id;
      this.inlineEditData = {
        title: urlItem.title || urlItem.name || '',
        description: urlItem.description || '',
        url: urlItem.url || '',
        tags: Array.isArray(urlItem.tags) ? [...urlItem.tags] : [],
        category: urlItem.category || 'webroll'
      };
    },

    cancelInlineEdit() {
      console.log('❌ Canceling inline edit');
      this.inlineEditingId = null;
      this.inlineEditData = {};
    },

    async saveInlineEdit(urlItem) {
      if (!this.inlineEditingId || !urlItem) return;
      
      console.log('💾 Saving inline edit:', this.inlineEditData);
      
      try {
        const knowledgeStore = useKnowledgeStore();
        
        // Check for duplicates (exclude current URL)
        const existingURL = knowledgeStore.allItems.find(item => 
          item.category === 'webroll' && 
          item.url === this.inlineEditData.url.trim() && 
          item.id !== urlItem.id
        );
        
        if (existingURL) {
          knowledgeStore.addNotification('duplicate-url', '❌ Duplicate URL', `URL already exists: ${existingURL.title}`);
          return;
        }
        
        // Update the URL item
        const updatedItem = {
          ...urlItem,
          title: this.inlineEditData.title.trim(),
          description: this.inlineEditData.description.trim(),
          url: this.inlineEditData.url.trim(),
          tags: this.inlineEditData.tags,
          category: this.inlineEditData.category,
          updatedAt: new Date().toISOString()
        };
        
        // Update in Firebase
        await knowledgeStore.updateItem('webroll', urlItem.id, updatedItem);
        
        knowledgeStore.addNotification('url-updated', '✅ URL Updated', 'URL updated successfully!');
        
        // Exit edit mode
        this.cancelInlineEdit();
        
      } catch (error) {
        console.error('❌ Error updating URL:', error);
        knowledgeStore.addNotification('update-error', '❌ Update Failed', 'Failed to update URL. Please try again.');
      }
    },

    async deleteURLInline(urlItem) {
      if (!urlItem) return;
      
      // Confirmation dialog
      const confirmed = confirm(`Are you sure you want to delete "${urlItem.title || urlItem.name}"?\n\nThis action cannot be undone.`);
      if (!confirmed) return;
      
      console.log('🗑️ Deleting URL inline:', urlItem);
      
      try {
        const knowledgeStore = useKnowledgeStore();
        
        // Delete from Firebase
        await knowledgeStore.deleteItem('webroll', urlItem.id);
        
        knowledgeStore.addNotification('url-deleted', '✅ URL Deleted', 'URL deleted successfully!');
        
      } catch (error) {
        console.error('❌ Error deleting URL:', error);
        knowledgeStore.addNotification('delete-error', '❌ Delete Failed', 'Failed to delete URL. Please try again.');
      }
    },

    // Tag management for inline editing
    removeTagInline(tagIndex) {
      this.inlineEditData.tags.splice(tagIndex, 1);
    },

    addTagInline() {
      const newTag = prompt('Enter a new tag (without #):');
      if (newTag && newTag.trim()) {
        const cleanTag = newTag.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
        if (cleanTag && !this.inlineEditData.tags.includes(cleanTag)) {
          this.inlineEditData.tags.push(cleanTag);
        }
      }
    },

    // Legacy modal methods - REMOVED (replaced by inline editing)

    // Legacy saveURLChanges and deleteURL methods - REMOVED (replaced by inline editing)

    // Helper methods for item context cards
    getItemIcon(category) {
      const icons = {
        note: '📝',
        project: '📁', 
        contact: '👤',
        idea: '💡',
        webroll: '🔗'
      };
      return icons[category] || '📄';
    },

    getItemTypeDisplay(category) {
      const displays = {
        note: 'Note',
        project: 'Project',
        contact: 'Contact',
        idea: 'Idea',
        webroll: 'URL'
      };
      return displays[category] || 'Item';
    },

    // Item inline editing methods (Notes, Projects, Contacts, Ideas)
    startItemInlineEdit(item) {
      console.log('✏️ Starting item inline edit:', item);
      this.inlineEditingItemId = item.id;
      
      // Base data for all item types
      this.inlineEditItemData = {
        title: item.title || item.name || '',
        description: item.description || item.content || '',
        category: item.category,
        tags: Array.isArray(item.tags) ? [...item.tags] : []
      };
      
      // Contact-specific fields
      if (item.category === 'contact') {
        this.inlineEditItemData.email = item.email || '';
        this.inlineEditItemData.phone = item.phone || '';
        this.inlineEditItemData.notes = item.notes || '';
      }
      
      // Store any other item-specific fields
      Object.keys(item).forEach(key => {
        if (!this.inlineEditItemData.hasOwnProperty(key)) {
          this.inlineEditItemData[key] = item[key];
        }
      });
    },

    cancelItemInlineEdit() {
      console.log('❌ Canceling item inline edit');
      this.inlineEditingItemId = null;
      this.inlineEditItemData = {};
    },

    async saveItemInlineEdit(item) {
      if (!this.inlineEditingItemId || !item) return;
      
      console.log('💾 Saving item inline edit:', this.inlineEditItemData);
      
      try {
        const knowledgeStore = useKnowledgeStore();
        
        // Base updated item
        const updatedItem = {
          ...item,
          title: this.inlineEditItemData.title.trim(),
          updatedAt: new Date().toISOString()
        };
        
        // Handle category-specific fields
        if (item.category === 'contact') {
          // Contact-specific fields
          updatedItem.name = this.inlineEditItemData.title.trim(); // Contacts use 'name' not 'title'
          updatedItem.email = this.inlineEditItemData.email.trim();
          updatedItem.phone = this.inlineEditItemData.phone.trim();
          updatedItem.notes = this.inlineEditItemData.notes.trim();
        } else if (item.category === 'note') {
          // Notes use 'content' field
          updatedItem.content = this.inlineEditItemData.description.trim();
          updatedItem.description = this.inlineEditItemData.description.trim();
        } else {
          // Projects and Ideas use 'description'
          updatedItem.description = this.inlineEditItemData.description.trim();
        }

        // Persist tags for all categories if provided
        if (Array.isArray(this.inlineEditItemData.tags)) {
          updatedItem.tags = this.inlineEditItemData.tags.map(t => String(t).trim().toLowerCase()).filter(Boolean);
        }
        
        // Update in Firebase - use correct plural category name
        const categoryMap = {
          'note': 'notes',
          'project': 'projects',
          'contact': 'contacts', 
          'idea': 'ideas'
        };
        const correctCategory = categoryMap[item.category] || item.category;
        
        await knowledgeStore.updateItem(correctCategory, item.id, updatedItem);
        
        knowledgeStore.addNotification('item-updated', '✅ Item Updated', `${this.getItemTypeDisplay(item.category)} updated successfully!`);
        
        // Exit edit mode
        this.cancelItemInlineEdit();
        
        // If we came from showcase, keep showcase visible and clear the editing state
        if (this.internalSelectedItem) {
          // Clear internal selection but keep showcase visible
          this.internalSelectedItem = null;
          // Keep lastAction as 'item-showcase' to maintain the list
        }
        
      } catch (error) {
        console.error('❌ Error updating item:', error);
        knowledgeStore.addNotification('update-error', '❌ Update Failed', 'Failed to update item. Please try again.');
      }
    },

    async deleteItemInline(item) {
      if (!item) return;
      
      // Confirmation dialog
      const confirmed = confirm(`Are you sure you want to delete this ${item.category}?\n\n"${item.title || item.name}"\n\nThis action cannot be undone.`);
      if (!confirmed) return;
      
      console.log('🗑️ Deleting item inline:', item);
      
      try {
        const knowledgeStore = useKnowledgeStore();
        
        // Delete from Firebase - use correct plural category name
        const categoryMap = {
          'note': 'notes',
          'project': 'projects',
          'contact': 'contacts', 
          'idea': 'ideas'
        };
        const correctCategory = categoryMap[item.category] || item.category;
        
        await knowledgeStore.deleteItem(correctCategory, item.id);
        
        knowledgeStore.addNotification('item-deleted', '✅ Item Deleted', `${this.getItemTypeDisplay(item.category)} deleted successfully!`);
        
        // Close the modal since the item is gone
        this.$emit('close');
        
      } catch (error) {
        console.error('❌ Error deleting item:', error);
        knowledgeStore.addNotification('delete-error', '❌ Delete Failed', 'Failed to delete item. Please try again.');
      }
    },
    
    askAIAboutSite() {
      const prompt = `Analyze this website: ${this.webviewURL}\n\nCan you tell me about this site? What is it about, what can I find there, and any recommendations?`;
      this.closeWebview();
      this.showAICommand = true;
      this.lastAction = 'ai';
      this.aiPrompt = prompt;
      
      this.$nextTick(() => {
        const textarea = this.$refs.aiTextarea;
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      });
    },
    
    extractDomain(url) {
      try {
        return new URL(url).hostname.replace('www.', '');
      } catch {
        return 'Website';
      }
    },
    
    askAIAboutURLs() {
      // Create a smart prompt about the current URLs
      const urlList = this.showcaseURLs.map(url => `• ${url.title}: ${url.url}`).join('\n');
      const smartPrompt = `Analyze these ${this.showcaseTag} URLs and provide insights:\n\n${urlList}\n\nWhat can you tell me about these websites? Any patterns, recommendations, or summaries?`;
      
      // Transition from URL showcase to AI command
      this.dismissURLShowcase();
      this.showAICommand = true;
      this.lastAction = 'ai';
      this.aiPrompt = smartPrompt;
      
      // Auto-focus on the AI textarea
      this.$nextTick(() => {
        const textarea = this.$refs.aiTextarea;
        if (textarea) {
          textarea.focus();
          // Put cursor at the end
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      });
    },
    
    // 🎯 Bulk Import Methods
    async processBulkImport() {
      if (!this.bulkURLs.trim()) return;
      
      this.bulkImportLoading = true;
      this.lastAction = 'bulk-import';
      
      try {
        console.log('📦 Processing bulk import...');
        
        // Parse URLs from textarea
        const urls = this.bulkURLs.split('\n')
          .map(line => line.trim())
          .filter(line => line && (line.startsWith('http://') || line.startsWith('https://')));
        
        console.log(`📦 Found ${urls.length} valid URLs`);
        
        if (urls.length === 0) {
          throw new Error('No valid URLs found. Please paste URLs starting with http:// or https://');
        }
        
        // 🎯 STEP 4A: AI Analysis with title fetching
        console.log('🤖 Starting AI analysis of URLs...');
        
        // 🎮 Gamification: Show expected vs actual time
        const expectedTimePerURL = 5; // seconds
        const expectedTotal = urls.length * expectedTimePerURL;
        const startTime = Date.now();
        this.importStartTime = startTime;
        // Reactive countdown fields
        this.expectedTime = expectedTotal;
        this.liveTime = 0;
        this.showCountdown = true;
        this.processingProgress = 'Starting…';

        console.log(`⏱️ Expected processing time: ${expectedTotal}s for ${urls.length} URLs`);
        
        const aiAnalysisPrompt = `Analyze these ${urls.length} URLs and suggest smart tags for each. Focus on categorizing by content type, domain purpose, and use case.

URLs to analyze:
${urls.map((url, index) => `${index + 1}. ${url}`).join('\n')}

For each URL, provide:
1. A concise, descriptive title (if you can infer from domain/path)
2. 2-4 relevant tags (e.g., "development", "design", "news", "tools", "documentation", "learning", "productivity", "business", "tech", etc.)
3. Brief description of what the site/page is about

Format your response as JSON array:
[
  {
    "url": "https://example.com",
    "title": "Example Site",
    "tags": ["development", "tools"],
    "description": "A development tool for..."
  }
]

Be smart about inferring content from domains like github.com (development), youtube.com (videos), docs.* (documentation), etc.`;

        // Send to AI for analysis
        const aiResponse = await geminiService.getAIResponse(aiAnalysisPrompt);
        
        const actualTime = Math.round((Date.now() - startTime) / 1000);
        this.liveTime = actualTime;
        this.isBeatExpected = actualTime <= expectedTotal;
        this.processingProgress = 'Analysis complete';
        console.log(`⚡ Actual processing time: ${actualTime}s (Expected: ${expectedTotal}s)`);
        console.log('🤖 AI analysis complete:', aiResponse);
        
        // 🎯 STEP 4B: Parse AI response (IMPROVED)
        let processedURLs = [];
        try {
          // Handle the backend response format: {success: true, response: "...", timestamp: "..."}
          let aiResponseText = aiResponse;
          if (typeof aiResponse === 'string' && aiResponse.includes('"response"')) {
            try {
              const parsed = JSON.parse(aiResponse);
              if (parsed.response) {
                aiResponseText = parsed.response;
              }
            } catch (e) {
              // If parsing fails, use original response
              aiResponseText = aiResponse;
            }
          }
          
          // Try multiple parsing strategies for different AI response formats
          let jsonContent = null;
          
          // Strategy 1: Look for ```json ... ``` format
          const codeBlockMatch = aiResponseText.match(/```json\s*([\s\S]*?)\s*```/);
          if (codeBlockMatch) {
            jsonContent = codeBlockMatch[1].trim();
            console.log('📝 Found JSON in code block');
          } else {
            // Strategy 2: Look for JSON array pattern
            const jsonArrayMatch = aiResponseText.match(/\[[\s\S]*?\]/);
            if (jsonArrayMatch) {
              jsonContent = jsonArrayMatch[0];
              console.log('📝 Found JSON array pattern');
            } else {
              // Strategy 3: Parse from structured text (fallback)
              console.log('📝 No JSON found, parsing structured text...');
              jsonContent = this.parseStructuredTextToJSON(aiResponseText, urls);
            }
          }
          
          if (jsonContent && typeof jsonContent === 'string') {
            const aiResults = JSON.parse(jsonContent);
            processedURLs = aiResults.map((result, index) => ({
              url: urls[index] || result.url,
              title: result.title || this.extractDomain(urls[index]),
              tags: Array.isArray(result.tags) ? result.tags : ['webroll'],
              description: result.description || '',
              aiGenerated: true
            }));
          } else if (Array.isArray(jsonContent)) {
            processedURLs = jsonContent;
          } else {
            throw new Error('Could not parse AI response in any format');
          }
        } catch (parseError) {
          console.warn('⚠️ AI response parsing failed, using fallback:', parseError);
          // Fallback: basic processing without AI
          processedURLs = urls.map(url => ({
            url,
            title: this.extractDomain(url),
            tags: ['webroll'],
            description: '',
            aiGenerated: false
          }));
        }
        
        console.log('✅ Processed URLs:', processedURLs);
        
        // 🎯 STEP 5: Show preview (next step)
        // TODO: Preview with editable tags (HITL)
        // 🎯 STEP 6: Batch Firebase upload (next step)
        // TODO: Batch Firebase upload
        
        // For now, show success with preview
        this.bulkImportResults = {
          totalUrls: urls.length,
          processedUrls: processedURLs,
          processed: processedURLs.length,
          categories: [...new Set(processedURLs.flatMap(u => u.tags))],
          message: `🤖 AI analyzed ${urls.length} URLs and suggested ${[...new Set(processedURLs.flatMap(u => u.tags))].length} categories!`
        };
        
        // Keep form open to show results
        // this.bulkURLs = '';
        // this.showBulkImportForm = false;
        
      } catch (error) {
        console.error('❌ Bulk import error:', error);
        alert(`Error: ${error.message}`);
      } finally {
        this.bulkImportLoading = false;
        this.showCountdown = false;
      }
    },
    
      cancelBulkImport() {
    this.showBulkImportForm = false;
    this.bulkURLs = '';
    this.bulkImportLoading = false;
    this.bulkImportResults = null;
    this.lastAction = 'ai'; // Return to AI
  },
  
  // 🎯 Step 6: Firebase Upload
  async confirmAndImport() {
    if (!this.bulkImportResults?.processedUrls) return;
    
    try {
      console.log('🔥 Starting Firebase upload...');
      const urlsToImport = this.bulkImportResults.processedUrls;
      
      // Upload each URL to Firebase with AI-generated tags
      for (const urlData of urlsToImport) {
        await this.knowledgeStore.addItemToCategory('webroll', {
          title: urlData.title,
          url: urlData.url,
          type: 'url',
          tags: urlData.tags,
          description: urlData.description || `URL: ${urlData.url}`,
          aiGenerated: urlData.aiGenerated,
          importedAt: new Date().toISOString()
        });
        console.log(`✅ Imported: ${urlData.title}`);
      }
      
      // Success feedback
      this.knowledgeStore.addNotification('import-success', 
        `🎉 Successfully imported ${urlsToImport.length} URLs to your webroll!`);
      
      // 🎯 Create separate success card at top of feed
      this.showImportSuccess = true;
      this.lastAction = 'import-success';
      this.showAICommand = false; // Hide AI Assistant to ensure success card is on top
      // Bring success card to top and remount
      this.bumpOrder('import-success');
      this.importSuccessKey++;
      this.lastImportResults = {
        importedCount: urlsToImport.length,
        categories: this.bulkImportResults.categories,
        successMessage: `🎉 Successfully imported ${urlsToImport.length} URLs to your webroll!`,
        processingTime: `${Math.round((Date.now() - this.importStartTime) / 1000)}s`
      };
      
      // Hide the bulk import form now that we have success card
      this.showBulkImportForm = false;
      this.bulkURLs = '';
      this.bulkImportResults = null;
      
      console.log('🎉 Bulk import completed successfully!');
      
    } catch (error) {
      console.error('❌ Firebase upload error:', error);
      alert(`Import failed: ${error.message}`);
    }
  },
  
  editMoreTags() {
    // Hide the preview to allow more editing
    this.bulkImportResults = null;
    console.log('✏️ Allowing more tag editing');
  },
  
  addCustomTag(urlIndex) {
    // Prompt user for custom tag
    const customTag = prompt('Enter a custom tag (without #):');
    if (customTag && customTag.trim()) {
      const cleanTag = customTag.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (cleanTag) {
        // Add the tag to the specific URL
        if (!this.bulkImportResults.processedUrls[urlIndex].tags.includes(cleanTag)) {
          this.bulkImportResults.processedUrls[urlIndex].tags.push(cleanTag);
          console.log(`✅ Added custom tag #${cleanTag} to URL ${urlIndex}`);
        } else {
          alert('Tag already exists for this URL');
        }
      }
    }
  },
  
  // 🎉 Success Card Actions
  async viewWebroll() {
    // Show ALL webroll URLs (not just tagged ones)
    try {
      const allItems = this.knowledgeStore.allItems;
      const webrollItems = allItems.filter(item => 
        item.category === 'webroll' || 
        (item.tags && item.tags.includes('webroll'))
      );
      
      console.log(`🔍 Found ${webrollItems.length} total webroll URLs`);
      
      if (webrollItems.length === 0) {
        this.aiResponse = '❌ No URLs found in your webroll yet.';
        this.showAICommand = true;
        this.lastAction = 'ai';
      } else {
        // Show all webroll URLs
        this.showURLsByCategory(webrollItems, 'webroll', 'webroll');
        // Success card should stay in feed as history - don't dismiss it!
      }
    } catch (error) {
      console.error('❌ Error viewing webroll:', error);
      this.aiResponse = `❌ Error retrieving webroll: ${error.message}`;
      this.showAICommand = true;
      this.lastAction = 'ai';
      this.closeImportSession();
    }
  },
  
  askAIAboutImported() {
    // Set up AI prompt about the imported URLs
    this.aiPrompt = `Tell me about the ${this.bulkImportResults.importedCount} URLs I just imported to my webroll. What can you help me do with them?`;
    this.showAICommand = true;
    this.lastAction = 'ai';
    this.closeImportSession();
  },
  
  startNewImport() {
    // Reset for new import session
    this.bulkImportResults = null;
    this.bulkURLs = '';
    // Keep the form open for new URLs
  },
  
  closeImportSession() {
    // Close everything and return to main interface
    this.showBulkImportForm = false;
    this.bulkURLs = '';
    this.bulkImportResults = null;
    this.lastAction = 'ai';
  },
  
  // 🎉 Success Card Actions (for the separate top card)
  async viewWebrollFromSuccess() {
    // Same logic as viewWebroll but dismiss success card after
    try {
      const allItems = this.knowledgeStore.allItems;
      const webrollItems = allItems.filter(item => 
        item.category === 'webroll' || 
        (item.tags && item.tags.includes('webroll'))
      );
      
      console.log(`🔍 Found ${webrollItems.length} total webroll URLs`);
      
      if (webrollItems.length === 0) {
        this.aiResponse = '❌ No URLs found in your webroll yet.';
        this.showAICommand = true;
        this.lastAction = 'ai';
      } else {
        // Show all webroll URLs
        this.showURLsByCategory(webrollItems, 'webroll', 'webroll');
        // Success card should stay in feed as history - don't dismiss it!
      }
    } catch (error) {
      console.error('❌ Error viewing webroll:', error);
      this.aiResponse = `❌ Error retrieving webroll: ${error.message}`;
      this.showAICommand = true;
      this.lastAction = 'ai';
      this.dismissImportSuccess();
    }
  },
  
  askAIAboutImportedFromSuccess() {
    // Set up AI prompt about the imported URLs
    this.aiPrompt = `Tell me about the ${this.lastImportResults.importedCount} URLs I just imported to my webroll. What can you help me do with them?`;
    this.showAICommand = true;
    this.lastAction = 'ai';
    // Keep success card in feed - don't dismiss!
  },
  
  startNewImportFromSuccess() {
    // Reset for new import session
    this.showBulkImportForm = true;
    this.lastAction = 'bulk-import';
    this.bulkURLs = '';
    this.bulkImportResults = null;
    // Keep success card in feed - don't dismiss!
  },
  
  dismissImportSuccess() {
    // Close the success card
    this.showImportSuccess = false;
    this.lastImportResults = null;
  },
  

  
  // 🎯 Helper: Parse structured AI text into JSON when direct JSON parsing fails
  parseStructuredTextToJSON(text, urls) {
    console.log('🔍 Parsing structured text response...');
    
    const results = [];
    
    // Extract each URL section from the structured text
    const sections = text.split(/\*\*\d+\./);
    
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const domain = this.extractDomain(url);
      
      // Find the section that mentions this URL or domain
      let relevantSection = sections.find(section => 
        section.includes(url) || section.includes(domain)
      );
      
      if (!relevantSection && sections[i + 1]) {
        relevantSection = sections[i + 1]; // Use position-based matching
      }
      
      let title = domain;
      let tags = ['webroll'];
      let description = '';
      
      if (relevantSection) {
        // Extract title (first line after **)
        const titleMatch = relevantSection.match(/\*\*([^*]+)\*\*/);
        if (titleMatch) {
          title = titleMatch[1].trim();
        }
        
        // Extract tags (look for backticks)
        const tagMatches = relevantSection.match(/`([^`]+)`/g);
        if (tagMatches) {
          tags = tagMatches.map(tag => tag.replace(/`/g, ''));
        }
        
        // Extract description (after "Description:")
        const descMatch = relevantSection.match(/Description:\*\*\s*([^*]+)/);
        if (descMatch) {
          description = descMatch[1].trim();
        }
      }
      
      results.push({
        url,
        title,
        tags,
        description
      });
    }
    
    console.log('✅ Parsed structured text:', results);
    return results;
  },
    // Tag Filter Bar actions
    addActiveTag(tag) {
      const t = String(tag).toLowerCase();
      if (!this.activeTagFilters.includes(t)) {
        this.activeTagFilters.push(t);
        this.bumpOrder('filter-bar');
      }
    },
    removeActiveTag(tag) {
      const t = String(tag).toLowerCase();
      this.activeTagFilters = this.activeTagFilters.filter(x => x !== t);
    },
    clearActiveTags() {
      this.activeTagFilters = [];
    }
  },
  mounted() {
    // Prevent background scrolling
    document.body.style.overflow = 'hidden';
    
    // Initialize role store
    this.rolesStore.initializeUserRole();
    
    // Pre-populate profile data from user info if available
    if (this.user) {
      this.profileData.fullName = this.user.displayName || '';
      this.profileData.email = this.user.email || '';
    }
    
    // Auto-start profile setup if user is logged in (setup flow)
    if (this.user && !this.setupComplete && this.isInitialSetup) {
      setTimeout(() => {
        this.startProfileSetup();
      }, 1500); // Small delay to show welcome first
    }

    // Handle selectedURLItem from carousel click
    if (this.selectedURLItem) {
      console.log('🔗 Modal opened with selected URL:', this.selectedURLItem);
      // Show URL showcase with just this URL
      this.showURLsByCategory([this.selectedURLItem], 'selected', 'webroll');
    }

    // Handle selectedItem from carousel click (notes, projects, etc.)
    if (this.selectedItem) {
      console.log('📄 Modal opened with selected item:', this.selectedItem);
      // Show item context card persistently and bring it to top
      this.showItemContext = true;
      this.bumpOrder('item-context');
      this.itemContextKey++;
    }
  },
  beforeUnmount() {
    // Restore background scrolling
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.welcome-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow: hidden;
  padding: 20px;
  box-sizing: border-box;
}

.modal-content {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.95) 0%, rgba(42, 82, 152, 0.95) 50%, rgba(62, 95, 159, 0.95) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  padding: 50px 30px 30px 30px;
  border-radius: 15px;
  position: relative;
  max-width: 600px;
  width: 100%;
  min-height: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.posts-container {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #4CAF50 #111;
  flex: 1;
  padding-top: 0;
  -webkit-overflow-scrolling: touch;
  padding-right: 10px;
}

.posts-container::-webkit-scrollbar {
  width: 8px;
}

.posts-container::-webkit-scrollbar-track {
  background: #111;
}

.posts-container::-webkit-scrollbar-thumb {
  background-color: #4CAF50;
  border-radius: 4px;
}

.post {
  text-align: center;
  transition: all 0.3s ease;
  opacity: 1;
  margin-bottom: 20px;
  cursor: pointer;
  padding: 15px;
  border-radius: 8px;
}

.post.fade {
  opacity: 0.3;
}

.post:hover {
  opacity: 1 !important;
  background: rgba(255, 255, 255, 0.05);
}

.profile-picture {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  margin: 20px auto;
  object-fit: cover;
  border: 3px solid #4CAF50;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  color: white;
  font-size: 30px;
  cursor: pointer;
  z-index: 1002;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.upload-post {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.action-button {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 15px;
  transition: background-color 0.3s;
}

.action-button:hover {
  background-color: #45a049;
}

/* Cancel Button Styling - Glass Morphism with Reddish Theme */
.cancel-button {
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.9), rgba(255, 77, 77, 0.9));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 24px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 15px;
  transition: all 0.3s ease;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.2);
}

.cancel-button:hover {
  background: linear-gradient(135deg, rgba(255, 77, 77, 0.9), rgba(255, 51, 51, 0.9));
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 107, 107, 0.3);
  border-color: rgba(255, 255, 255, 0.3);
}

h2 {
  margin: 0;
  font-size: 24px;
}

h3 {
  margin: 0 0 15px;
  font-size: 20px;
}

p {
  margin-top: 10px;
  opacity: 0.8;
}

.role-badge {
  background-color: #4CAF50;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  display: inline-block;
  margin-top: 15px;
}

.permission-message {
  color: #ffa500;
  font-style: italic;
  font-size: 0.9rem;
}

/* Mobile-specific adjustments */
@media (max-width: 768px) {
  .welcome-modal {
    padding: 10px;
  }
  
  .modal-content {
    max-width: 100%;
    width: 100%;
    padding: 45px 20px 20px 20px;
    min-height: 70vh;
    max-height: 90vh;
    border-radius: 10px;
  }

  .ai-textarea {
    min-height: 100px;
    font-size: 16px; /* Prevents zoom on iOS */
  }
  
  .ai-execute-btn {
    padding: 12px 24px;
    font-size: 15px;
  }
  
  .ai-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .ai-actions button {
    width: 100%;
    margin-bottom: 10px;
  }
}

@media (max-width: 480px) {
  .modal-content {
    padding: 40px 15px 15px 15px;
    min-height: 75vh;
  }
  
  .close-button {
    top: 10px;
    right: 10px;
    font-size: 24px;
    width: 35px;
    height: 35px;
  }
  
  .profile-picture {
    width: 80px;
    height: 80px;
  }
  
  h2 {
    font-size: 20px;
  }
  
  h3 {
    font-size: 18px;
  }

  .ai-label {
    font-size: 15px;
  }

  .ai-textarea {
    padding: 12px;
    font-size: 16px;
    min-height: 90px;
  }
}

/* iOS Safari specific fixes */
@supports (-webkit-touch-callout: none) {
  .modal-content {
    min-height: 60vh;
    max-height: 85vh;
  }
  
  .ai-textarea {
    -webkit-appearance: none;
    border-radius: 8px;
  }
}

/* Setup Form Styling */
.setup-form {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  color: #4CAF50;
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 14px;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.form-group input::placeholder {
  color: #666;
}

.setup-complete-post ul {
  text-align: left;
  max-width: 300px;
  margin: 15px auto;
}

.setup-complete-post li {
  margin-bottom: 8px;
  color: #ccc;
}

.setup-info {
  color: #4CAF50;
  font-style: italic;
  font-size: 14px;
  margin-top: 10px;
}

.add-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  max-width: 300px;
  margin: 20px auto;
}

.option-button {
  background-color: #222;
  border: 2px solid #4CAF50;
  color: white;
  padding: 15px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.option-button:hover {
  background-color: #4CAF50;
  transform: translateY(-2px);
}

/* AI Command Post Enhancements */
.ai-command-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.ai-input-group {
  margin-bottom: 20px;
}

.ai-label {
  display: block;
  color: #4CAF50;
  font-weight: bold;
  margin-bottom: 8px;
  font-size: 16px;
}

.ai-textarea {
  width: 100%;
  padding: 15px;
  border: 2px solid #333;
  border-radius: 8px;
  background-color: #0a0a0a;
  color: white;
  font-size: 15px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  min-height: 120px;
  box-sizing: border-box;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.ai-textarea:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
}

.ai-textarea::placeholder {
  color: #888;
  font-style: italic;
}

.ai-execute-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  font-size: 16px;
  padding: 14px 28px;
  font-weight: 600;
}

.ai-execute-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.ai-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.ai-response {
  margin-top: 20px;
  padding: 15px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
  border-left: 4px solid #4CAF50;
}

.ai-response h4 {
  margin: 0 0 10px 0;
  color: #4CAF50;
  font-size: 16px;
}

.ai-response p {
  margin: 0;
  line-height: 1.6;
}

/* Note Context Card Styling */
.note-context-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.note-form {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.note-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 10px;
}

.note-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.note-input::placeholder {
  color: #666;
}

.note-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;
}

.note-textarea:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.note-textarea::placeholder {
  color: #666;
}

.note-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 15px;
}

.save-note-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
}

.save-note-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Note Success Post Styling */
.note-success-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.success-content {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.created-note h4 {
  margin: 0 0 10px 0;
  color: #4CAF50;
  font-size: 18px;
}

.note-preview {
  font-style: italic;
  color: #ccc;
  font-size: 14px;
  margin-bottom: 10px;
}

.timestamp {
  font-size: 12px;
  color: #888;
  margin-top: 5px;
}

.category-status p {
  font-size: 14px;
  color: #ccc;
  margin-top: 10px;
}

.success-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.dismiss-button {
  background: linear-gradient(135deg, #ff6b6b, #ff4f4f);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.dismiss-button:hover {
  background: linear-gradient(135deg, #ff4f4f, #ff3333);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

/* Sticky Command Bar Styling */
.sticky-command-bar {
  position: fixed;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.9), rgba(42, 82, 152, 0.9));
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 8px 20px;
  z-index: 1001;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
}

.command-options {
  display: flex;
  gap: 15px;
  align-items: center;
}

.command-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
}

.command-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.1);
  color: #4CAF50;
}

.ai-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
}

.ai-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  color: white;
  transform: scale(1.1);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .sticky-command-bar {
    bottom: 10px;
    padding: 6px 15px;
  }
  
  .command-btn {
    font-size: 18px;
    min-width: 35px;
    min-height: 35px;
    padding: 6px;
  }
  
  .command-options {
    gap: 12px;
  }
}

/* Getting Started Post Styling */
.getting-started-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
}

.getting-started-post h3 {
  margin-bottom: 15px;
  font-size: 24px;
  color: #4CAF50;
}

.getting-started-post > p {
  margin-bottom: 20px;
  color: #ccc;
  font-size: 16px;
}

.command-guide {
  display: grid;
  gap: 15px;
  margin-bottom: 20px;
  text-align: left;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.command-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.command-item:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(76, 175, 80, 0.3);
  transform: translateY(-1px);
}

.guide-icon {
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 50%;
  flex-shrink: 0;
}

.guide-text {
  flex: 1;
}

.guide-text strong {
  display: block;
  color: white;
  font-size: 16px;
  margin-bottom: 4px;
}

.guide-text p {
  color: #aaa;
  font-size: 14px;
  margin: 0;
  line-height: 1.4;
}

.getting-started-footer {
  padding-top: 15px;
  padding-bottom: 25px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  margin-bottom: 20px;
}

.getting-started-footer p {
  color: #888;
  font-size: 14px;
  margin: 0;
  font-style: italic;
}

/* Mobile responsive for getting started */
@media (max-width: 768px) {
  .getting-started-post {
    padding: 15px;
  }
  
  .getting-started-post h3 {
    font-size: 20px;
  }
  
  .command-item {
    padding: 10px;
    gap: 12px;
  }
  
  .guide-icon {
    font-size: 20px;
    width: 35px;
    height: 35px;
  }
  
  .guide-text strong {
    font-size: 14px;
  }
  
  .guide-text p {
    font-size: 12px;
  }
  
  .getting-started-footer {
    padding-bottom: 30px;
    margin-bottom: 25px;
  }
}

/* Compact Sticky User Header Bar Styling */
.user-header-bar {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.9), rgba(42, 82, 152, 0.9));
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 999;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #4CAF50;
  object-fit: cover;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 16px;
  font-weight: bold;
  color: white;
}

.user-role {
  font-size: 12px;
  color: #ccc;
  opacity: 0.8;
}

.setup-continue-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.3s;
}

.setup-continue-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Mobile responsive for user header */
@media (max-width: 768px) {
  .user-header-bar {
    padding: 8px 15px;
  }
  
  .user-avatar {
    width: 35px;
    height: 35px;
  }
  
  .user-info {
    gap: 12px;
  }
  
  .user-name {
    font-size: 14px;
  }
  
  .user-role {
    font-size: 11px;
  }
  
  .setup-continue-btn {
    font-size: 12px;
    padding: 6px 12px;
  }
}

/* Project Context Card Styling */
.project-context-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.project-form {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.project-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 10px;
}

.project-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.project-input::placeholder {
  color: #666;
}

.project-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;
}

.project-textarea:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.project-textarea::placeholder {
  color: #666;
}

.project-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 15px;
}

.save-project-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
}

.save-project-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Project Success Post Styling */
.project-success-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.project-success-content {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.created-project h4 {
  margin: 0 0 10px 0;
  color: #4CAF50;
  font-size: 18px;
}

.project-preview {
  font-style: italic;
  color: #ccc;
  font-size: 14px;
  margin-bottom: 10px;
}

.project-timestamp {
  font-size: 12px;
  color: #888;
  margin-top: 5px;
}

.project-category-status p {
  font-size: 14px;
  color: #ccc;
  margin-top: 10px;
}

.project-success-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.project-dismiss-button {
  background: linear-gradient(135deg, #ff6b6b, #ff4f4f);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.project-dismiss-button:hover {
  background: linear-gradient(135deg, #ff4f4f, #ff3333);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

/* Contact Context Card Styling */
.contact-context-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.contact-form {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.contact-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 10px;
}

.contact-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.contact-input::placeholder {
  color: #666;
}

.contact-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 15px;
}

.save-contact-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
}

.save-contact-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Contact Success Post Styling */
.contact-success-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.contact-success-content {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.created-contact h4 {
  margin: 0 0 10px 0;
  color: #4CAF50;
  font-size: 18px;
}

.contact-preview {
  font-style: italic;
  color: #ccc;
  font-size: 14px;
  margin-bottom: 10px;
}

.contact-timestamp {
  font-size: 12px;
  color: #888;
  margin-top: 5px;
}

.contact-category-status p {
  font-size: 14px;
  color: #ccc;
  margin-top: 10px;
}

.contact-success-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.contact-dismiss-button {
  background: linear-gradient(135deg, #ff6b6b, #ff4f4f);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.contact-dismiss-button:hover {
  background: linear-gradient(135deg, #ff4f4f, #ff3333);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

/* Idea Context Card Styling */
.idea-context-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.idea-form {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.idea-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 10px;
}

.idea-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.idea-input::placeholder {
  color: #666;
}

.idea-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #333;
  border-radius: 5px;
  background-color: #111;
  color: white;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;
}

.idea-textarea:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.idea-textarea::placeholder {
  color: #666;
}

.idea-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 15px;
}

.save-idea-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
}

.save-idea-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* Idea Success Post Styling */
.idea-success-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.idea-success-content {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.created-idea h4 {
  margin: 0 0 10px 0;
  color: #4CAF50;
  font-size: 18px;
}

.idea-preview {
  font-style: italic;
  color: #ccc;
  font-size: 14px;
  margin-bottom: 10px;
}

.idea-timestamp {
  font-size: 12px;
  color: #888;
  margin-top: 5px;
}

.idea-category-status p {
  font-size: 14px;
  color: #ccc;
  margin-top: 10px;
}

.idea-success-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.idea-dismiss-button {
  background: linear-gradient(135deg, #ff6b6b, #ff4f4f);
  font-size: 14px;
  padding: 8px 16px;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.idea-dismiss-button:hover {
  background: linear-gradient(135deg, #ff4f4f, #ff3333);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

/* URL Showcase Post Styling */
.url-showcase-post {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 20px;
  border-radius: 12px;
}

.showcase-header {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.showcase-header p {
  margin: 0;
  color: #ccc;
  font-size: 14px;
}

.url-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.url-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.url-card:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-color: rgba(76, 175, 80, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.2);
}

.url-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.url-edit-btn {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 14px;
  opacity: 0.7;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  min-height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .url-edit-btn {
    padding: 10px 12px;
    font-size: 16px;
    border-radius: 8px;
    min-height: 44px;
    min-width: 44px;
  }
}

.url-edit-btn:hover {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%);
  border-color: rgba(76, 175, 80, 0.4);
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
}

/* URL Actions Container - Mobile Responsive */
.url-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .url-actions {
    gap: 12px;
    margin-left: 8px;
  }
}

/* Delete Button - Mobile Responsive */
.url-delete-btn {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 14px;
  opacity: 0.7;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  min-height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .url-delete-btn {
    padding: 10px 12px;
    font-size: 16px;
    border-radius: 8px;
    min-height: 44px;
    min-width: 44px;
  }
}

.url-delete-btn:hover {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(244, 67, 54, 0.1) 100%);
  border-color: rgba(244, 67, 54, 0.4);
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.2);
}

/* Inline Edit Styles - Mobile Responsive */
.inline-edit-title {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 12px;
  font-size: 16px;
  font-weight: bold;
  width: 100%;
  backdrop-filter: blur(10px);
  min-height: 44px; /* iOS recommended touch target */
  box-sizing: border-box;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .inline-edit-title {
    padding: 14px;
    font-size: 18px;
    border-radius: 8px;
    min-height: 48px;
  }
}

.inline-edit-title:focus {
  outline: none;
  border-color: #4CAF50;
  background: rgba(255, 255, 255, 0.15);
}

.inline-edit-url {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #81C784;
  padding: 10px 12px;
  font-size: 14px;
  width: 100%;
  margin-top: 8px;
  backdrop-filter: blur(10px);
  min-height: 44px;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .inline-edit-url {
    padding: 12px 14px;
    font-size: 16px;
    border-radius: 8px;
    min-height: 48px;
  }
}

.inline-edit-url:focus {
  outline: none;
  border-color: #4CAF50;
  background: rgba(255, 255, 255, 0.15);
}

.inline-edit-description {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 12px;
  font-size: 14px;
  width: 100%;
  margin-top: 8px;
  resize: vertical;
  min-height: 80px;
  backdrop-filter: blur(10px);
  box-sizing: border-box;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .inline-edit-description {
    padding: 14px;
    font-size: 16px;
    border-radius: 8px;
    min-height: 100px;
    line-height: 1.6;
  }
}

.inline-edit-description:focus {
  outline: none;
  border-color: #4CAF50;
  background: rgba(255, 255, 255, 0.15);
}

/* Inline Edit Tags */
.inline-edit-tags {
  margin-top: 8px;
}

.tags-display {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.tag-editable {
  background: rgba(76, 175, 80, 0.2);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.tag-remove {
  background: none;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.tag-remove:hover {
  background: rgba(255, 107, 107, 0.2);
}

.tag-add-btn {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 12px;
  color: #4CAF50;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .tag-add-btn {
    padding: 8px 16px;
    font-size: 14px;
    border-radius: 16px;
    min-height: 44px;
  }
}

.tag-add-btn:hover {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.3) 0%, rgba(76, 175, 80, 0.2) 100%);
  transform: translateY(-1px);
}

/* Inline Save/Cancel Buttons - Mobile Responsive */
.save-btn-inline, .cancel-btn-inline {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  opacity: 0.8;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  min-height: 44px;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .save-btn-inline, .cancel-btn-inline {
    padding: 12px 16px;
    font-size: 16px;
    border-radius: 8px;
    min-height: 48px;
    min-width: 48px;
  }
}

.save-btn-inline:hover {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%);
  border-color: rgba(76, 175, 80, 0.4);
  opacity: 1;
  transform: translateY(-1px);
}

.cancel-btn-inline:hover {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(244, 67, 54, 0.1) 100%);
  border-color: rgba(244, 67, 54, 0.4);
  opacity: 1;
  transform: translateY(-1px);
}

/* Item Showcase Styles - Similar to URL Grid */
.item-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.item-showcase-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.item-showcase-card:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-color: rgba(76, 175, 80, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.2);
}

/* Notes Showcase Cards - iPhone-inspired yellow background */
.item-showcase-card.note-showcase-card {
  background: linear-gradient(135deg, rgba(255, 235, 59, 0.15), rgba(255, 193, 7, 0.08));
  border-color: rgba(255, 235, 59, 0.2);
}

.item-showcase-card.note-showcase-card:hover {
  background: linear-gradient(135deg, rgba(255, 235, 59, 0.25), rgba(255, 193, 7, 0.15));
  border-color: rgba(255, 235, 59, 0.4);
  box-shadow: 0 6px 20px rgba(255, 235, 59, 0.2);
}

.item-showcase-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.item-showcase-title-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.item-showcase-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.item-showcase-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.item-showcase-title-with-icon h4 {
  margin: 0;
  color: white;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.item-showcase-content {
  margin-bottom: 10px;
}

.contact-info {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin: 4px 0;
}

.item-preview {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.4;
  margin: 8px 0;
}

.item-showcase-meta {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 10px;
}

.item-showcase-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
}

.expandable-content {
  position: relative;
}

.expand-hint {
  color: rgba(76, 175, 80, 0.8);
  font-size: 12px;
  font-style: italic;
  cursor: pointer;
  margin-left: 8px;
}

.expand-hint:hover {
  color: rgba(76, 175, 80, 1);
}

/* Mobile responsive for item showcase */
@media (max-width: 768px) {
  .item-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .item-showcase-card {
    padding: 12px;
  }
  
  .item-showcase-title-with-icon h4 {
    font-size: 18px;
  }
  
  .contact-info, .item-preview {
    font-size: 16px;
  }
  
  .item-showcase-date {
    font-size: 14px;
  }
  
  .item-showcase-actions {
    gap: 12px;
  }
  
  .expand-hint {
    font-size: 14px;
  }
}

/* Item Card Styles - Unified with URL Cards */
.item-card-context {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 15px;
}

.item-card-context:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-color: rgba(76, 175, 80, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.2);
}

/* Notes Card - iPhone-inspired yellow background */
.item-card-context.note-card {
  background: linear-gradient(135deg, rgba(255, 235, 59, 0.15), rgba(255, 193, 7, 0.08));
  border-color: rgba(255, 235, 59, 0.2);
}

.item-card-context.note-card:hover {
  background: linear-gradient(135deg, rgba(255, 235, 59, 0.25), rgba(255, 193, 7, 0.15));
  border-color: rgba(255, 235, 59, 0.4);
  box-shadow: 0 6px 20px rgba(255, 235, 59, 0.2);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.item-title-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.item-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.item-title-with-icon h4 {
  margin: 0;
  color: white;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.item-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.item-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 10px;
}

.item-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 10px;
}

.item-category {
  font-weight: 500;
}

.item-date {
  font-style: italic;
}

/* Item Action Buttons - Match URL card buttons */
.item-edit-btn, .item-delete-btn, .save-btn-inline, .cancel-btn-inline {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  backdrop-filter: blur(10px);
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-edit-btn:hover, .save-btn-inline:hover {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.3) 0%, rgba(76, 175, 80, 0.1) 100%);
  border-color: rgba(76, 175, 80, 0.5);
  transform: scale(1.05);
}

.item-delete-btn:hover, .cancel-btn-inline:hover {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.3) 0%, rgba(244, 67, 54, 0.1) 100%);
  border-color: rgba(244, 67, 54, 0.5);
  transform: scale(1.05);
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .item-card-context {
    padding: 12px;
  }
  
  .item-title-with-icon h4 {
    font-size: 18px;
  }
  
  .item-description {
    font-size: 16px;
  }
  
  .item-meta {
    font-size: 14px;
  }
  
  .item-edit-btn, .item-delete-btn, .save-btn-inline, .cancel-btn-inline {
    min-width: 44px;
    min-height: 44px;
    font-size: 16px;
  }
}

/* Item Inline Edit Styles - Mobile Responsive (for Notes, Projects, Contacts, Ideas) */
.inline-edit-item-title {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 12px;
  font-size: 16px;
  font-weight: bold;
  width: 100%;
  backdrop-filter: blur(10px);
  min-height: 44px;
  box-sizing: border-box;
}

.inline-edit-item-description {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 12px;
  font-size: 14px;
  width: 100%;
  margin-top: 8px;
  resize: vertical;
  min-height: 120px;
  backdrop-filter: blur(10px);
  box-sizing: border-box;
  line-height: 1.5;
}

/* Contact-specific display styles */
.contact-details {
  margin: 12px 0;
}

.contact-field {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.contact-label {
  font-weight: bold;
  color: rgba(255, 255, 255, 0.9);
  min-width: 80px;
  font-size: 14px;
}

.contact-value {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  word-break: break-word;
}

/* Contact-specific edit styles */
.contact-edit-fields {
  margin: 12px 0;
}

.contact-edit-field {
  margin-bottom: 12px;
}

.contact-edit-field label {
  display: block;
  color: rgba(255, 255, 255, 0.9);
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 14px;
}

.inline-edit-contact-field,
.inline-edit-contact-notes {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 12px;
  font-size: 14px;
  width: 100%;
  backdrop-filter: blur(10px);
  box-sizing: border-box;
}

.inline-edit-contact-field {
  min-height: 44px;
}

.inline-edit-contact-notes {
  resize: vertical;
  min-height: 60px;
  line-height: 1.4;
}

.inline-edit-item-title:focus,
.inline-edit-item-description:focus,
.inline-edit-contact-field:focus,
.inline-edit-contact-notes:focus {
  outline: none;
  border-color: #4CAF50;
  background: rgba(255, 255, 255, 0.15);
}

@media (max-width: 768px) {
  .inline-edit-item-title {
    padding: 14px;
    font-size: 18px;
    border-radius: 8px;
    min-height: 48px;
  }
  
  .inline-edit-item-description {
    padding: 14px;
    font-size: 16px;
    border-radius: 8px;
    min-height: 140px;
    line-height: 1.6;
  }
  
  .inline-edit-contact-field,
  .inline-edit-contact-notes {
    padding: 14px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  .inline-edit-contact-field {
    min-height: 48px;
  }
  
  .inline-edit-contact-notes {
    min-height: 80px;
    line-height: 1.6;
  }
  
  .contact-label {
    font-size: 16px;
    min-width: 90px;
  }
  
  .contact-value {
    font-size: 16px;
  }
  
  .contact-edit-field label {
    font-size: 16px;
  }
}

/* Item Action Buttons - Mobile Responsive */
.item-edit-btn, .item-delete-btn {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 14px;
  opacity: 0.7;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  min-height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-edit-btn:hover {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%);
  border-color: rgba(76, 175, 80, 0.4);
  opacity: 1;
  transform: translateY(-1px);
}

.item-delete-btn:hover {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(244, 67, 54, 0.1) 100%);
  border-color: rgba(244, 67, 54, 0.4);
  opacity: 1;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .item-edit-btn, .item-delete-btn {
    padding: 10px 12px;
    font-size: 16px;
    border-radius: 8px;
    min-height: 44px;
    min-width: 44px;
  }
}

.url-header h4 {
  margin: 0;
  color: #4CAF50;
  font-size: 16px;
  font-weight: 600;
}

.url-icon {
  font-size: 20px;
  opacity: 0.7;
}

.url-link {
  color: #64B5F6;
  font-size: 13px;
  margin-bottom: 8px;
  word-break: break-all;
  opacity: 0.9;
}

.url-description {
  color: #ccc;
  font-size: 14px;
  margin-bottom: 10px;
  line-height: 1.4;
  font-style: italic;
}

.url-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}

.tag {
  background: rgba(76, 175, 80, 0.2);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.url-date {
  color: #888;
  font-size: 12px;
  opacity: 0.8;
}

.showcase-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* 🖼️ URL Logo Styling in Feed Cards */
.url-title-with-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.url-logo-image {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  min-height: 24px !important;
  max-width: 24px !important;
  max-height: 24px !important;
  border-radius: 4px !important;
  object-fit: contain !important;
  background: rgba(255, 255, 255, 0.05) !important;
  padding: 2px !important;
  filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2)) !important;
  transition: transform 0.2s ease !important;
  box-sizing: border-box !important;
}

.url-logo-image:hover {
  transform: scale(1.1) !important;
}

.url-logo-emoji {
  font-size: 18px;
  opacity: 0.7;
  min-width: 24px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Mobile responsive for URL showcase */
@media (max-width: 768px) {
  .url-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .url-card {
    padding: 12px;
  }
  
  .showcase-header {
    flex-direction: column;
    gap: 10px;
  }
  
  .showcase-actions {
    flex-direction: column;
  }
}

/* In-App Webview Styling */
.webview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  padding: 20px;
  box-sizing: border-box;
  animation: webviewFadeIn 0.3s ease-out;
  /* 📍 Prevent background scrolling */
  overflow: hidden;
}

/* 📍 Lock body scroll when webview is open */
body.webview-open {
  overflow: hidden !important;
  position: fixed !important;
  width: 100% !important;
}

@keyframes webviewFadeIn {
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(10px);
  }
}

@keyframes webviewSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.webview-container {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.95) 0%, rgba(42, 82, 152, 0.95) 50%, rgba(62, 95, 159, 0.95) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 15px;
  position: relative;
  width: 90vw;
  height: 80vh;
  max-width: 1200px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;
  animation: webviewSlideIn 0.4s ease-out;
}

.webview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.webview-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
}

.webview-icon {
  font-size: 20px;
}

.webview-controls {
  display: flex;
  gap: 10px;
}

.webview-fix-btn,
.webview-refresh-btn,
.webview-close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.webview-fix-btn {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  margin-right: 8px;
  font-size: 14px;
  opacity: 0.7;
  transition: all 0.3s ease;
}

.webview-fix-btn:hover {
  background: linear-gradient(135deg, rgba(255, 170, 0, 0.2) 0%, rgba(255, 140, 0, 0.1) 100%);
  border-color: rgba(255, 170, 0, 0.4);
  transform: translateY(-1px);
  opacity: 1;
  box-shadow: 0 4px 12px rgba(255, 170, 0, 0.2);
}

.webview-refresh-btn:hover {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(76, 175, 80, 0.4);
  transform: translateY(-1px);
}

.webview-close-btn:hover {
  background: rgba(244, 67, 54, 0.2);
  border-color: rgba(244, 67, 54, 0.4);
  transform: translateY(-1px);
}

.webview-content {
  flex: 1;
  position: relative;
  background: white;
  border-radius: 0 0 15px 15px;
  overflow: hidden;
}

.webview-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #333;
  z-index: 1002;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(76, 175, 80, 0.3);
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

.webview-frame {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 0 0 15px 15px;
}

/* Mobile responsive webview */
@media (max-width: 768px) {
  .webview-overlay {
    padding: 10px;
  }
  
  .webview-container {
    width: 95vw;
    height: 85vh;
    max-width: none;
  }
  
  .webview-header {
    padding: 12px 15px;
  }
  
  .webview-title {
    font-size: 14px;
  }
  
  .webview-refresh-btn,
  .webview-close-btn {
    padding: 6px 10px;
    font-size: 14px;
  }
}

.webview-error {
  text-align: center;
  padding: 20px;
  border-radius: 8px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.2);
  margin-top: 20px;
}

.error-icon {
  font-size: 40px;
  color: #ff4f4f;
  margin-bottom: 10px;
}

.error-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.error-actions .action-button {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.error-actions .action-button:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
}

/* 📦 Bulk Import Styles */
.bulk-import-post {
  background: rgba(0, 123, 255, 0.1);
  border-left: 4px solid #007bff;
}

.bulk-import-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 15px;
}

.bulk-url-input {
  width: 100%;
  min-height: 200px;
  padding: 12px;
  border: 2px solid rgba(0, 123, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  resize: vertical;
  backdrop-filter: blur(10px);
}

.bulk-url-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.bulk-import-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(0, 123, 255, 0.1);
  border-radius: 6px;
  font-size: 14px;
}

.url-count {
  color: #007bff;
  font-weight: 600;
}

.bulk-import-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.import-btn {
  background: linear-gradient(135deg, #007bff, #0056b3);
  flex: 1;
}

.import-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0056b3, #003d82);
}

.import-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 🎯 AI Results Preview Styling */
.ai-results-preview {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.results-summary {
  font-size: 16px;
  color: #ccc;
  margin-bottom: 10px;
}

.suggested-categories {
  font-size: 14px;
  color: #888;
  margin-bottom: 15px;
}
.suggested-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 12px; /* add vertical space so pills don't touch the scrollbar */
  margin-bottom: 8px;
  /* Thinner scrollbar across browsers */
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.1) transparent; /* ~90% transparent */
}
.suggested-scroll::-webkit-scrollbar { height: 4px; }
.suggested-scroll::-webkit-scrollbar-track { background: transparent; }
.suggested-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 999px; }

.suggested-tag {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.processed-urls-list {
  font-size: 14px;
  color: #ccc;
  margin-bottom: 20px;
}

.url-preview-item {
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.05);
}

.url-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.url-title {
  font-weight: 600;
}

.ai-badge {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.url-preview-url {
  color: #64B5F6;
  font-size: 13px;
  margin-bottom: 5px;
}

.url-preview-description {
  color: #ccc;
  font-size: 14px;
  margin-bottom: 5px;
}

.url-preview-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 5px;
}

.editable-tag {
  background: rgba(255, 255, 255, 0.2);
  color: #4CAF50;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.add-tag-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.add-tag-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.import-confirmation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
}

/* 🎉 Import Success Card */
.import-success-card {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05));
  border: 2px solid rgba(76, 175, 80, 0.3);
  margin-top: 20px;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
}

.success-message {
  font-size: 16px;
  color: #4CAF50;
  margin-bottom: 15px;
  font-weight: 600;
}

.import-stats {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
}

.stat {
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
}

.success-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.success-actions .action-button.secondary {
  background: linear-gradient(135deg, #64B5F6, #42A5F5);
}

.confirm-import-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.confirm-import-btn:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.edit-more-tags-btn {
  background: linear-gradient(135deg, #ccc, #999);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.edit-more-tags-btn:hover {
  background: linear-gradient(135deg, #999, #666);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(100, 100, 100, 0.3);
}

.import-success-card {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.1), rgba(42, 82, 152, 0.05));
  border: 1px solid rgba(76, 175, 80, 0.2);
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.import-success-card h4 {
  margin: 0 0 10px 0;
  color: #4CAF50;
  font-size: 18px;
}

.success-message {
  font-size: 14px;
  color: #ccc;
  margin-bottom: 10px;
}

.import-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(0, 123, 255, 0.1);
  border-radius: 6px;
  font-size: 14px;
}

.stat {
  color: #007bff;
  font-weight: 600;
}

.success-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.action-button {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.action-button:hover {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.secondary {
  background: linear-gradient(135deg, #ccc, #999);
  color: white;
}

.secondary:hover {
  background: linear-gradient(135deg, #999, #666);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(100, 100, 100, 0.3);
}

 
.chooser-post {
  padding: 12px;
}
.chooser-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.chooser-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.06);
  color: #e9f1f7;
  cursor: pointer;
}
.chooser-pill:hover {
  transform: scale(1.03);
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.25);
}
.chooser-pill:active { transform: scale(0.98); }
.chooser-pills { transition: 0.15s; }
.pill-icon { opacity: 0.8; }
.pill-meta { opacity: 0.6; font-size: 12px; }
.chooser-actions { margin-top: 10px; }
.tagop-success-post { padding: 12px; }
.ai-disabled { filter: blur(1px); opacity: 0.6; pointer-events: none; transition: 0.2s; }
.ai-disabled-banner { margin-top: 8px; display: flex; gap: 8px; align-items: center; }
</style>